import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    buildNativePurchasePayload,
    buildWebPurchaseLink,
    clearAnonymousRankingIdentity,
    clearPremiumSnapshot,
    ensureAnonymousAppUserId,
    grantReviewerAccess,
    getBillingEntitlementIds,
    getConfiguredPaywallPlans,
    getConfiguredPaywallSelectedPlan,
    getNativePackagePlanKind,
    getPaywallScreenConfig,
    getUFOVGameConfig,
    getStoredAppUserId,
    getUFOVConfig,
    getUFOVProgressStorageKey,
    getUFOVLimits,
    isNativeUFOVShell,
    loadWebBillingContext,
    matchesPackageAliases,
    normalizeNativePackageOptions,
    parseBridgePayload,
    postToNativeBridge,
    readPremiumSnapshot,
    refreshWebPremiumState,
    submitReviewFeedback,
    syncPremiumSnapshotFromNative,
    trackOnboardingSectionOnce,
    trackTikTokEvent,
    updateOnboardingSession,
    UFOV_LEGACY_PROGRESS_STORAGE_KEYS,
    UFOV_PROGRESS_STORAGE_KEY,
    validateReviewerAccessCode,
    writePremiumSnapshot,
} from '../lib/ufovRuntime';
import ufovScenarioCatalog from '../lib/ufovScenarioCatalog';
import ufovSymbolSets from '../lib/ufovSymbolSets';
import ufovTrials from '../lib/ufovTrials';
import soptEstimatedAge from '../lib/soptEstimatedAge';
import brainGameLevels from '../lib/brainGameLevels';
import brainGameDailyProgress from '../lib/brainGameDailyProgress';
import brainGameHighScores from '../lib/brainGameHighScores';
import gameScoreHistory from '../lib/gameScoreHistory';
import {
    detectPreferredUFOVLanguage,
    getUFOVLanguageDirection,
    isIndonesianUFOVAudience,
    normalizeUFOVLanguage,
} from '../i18n/ufovLocales';
import {
    UFOVCenterQuestionScreen,
    getUFOVGameCopy,
    UFOVGameStyles,
    UFOVOnboardingAgeOptInScreen,
    UFOVOnboardingAgeScreen,
    UFOVOnboardingDemographicsScreen,
    UFOVOnboardingPanelScreen,
    UFOVOnboardingReviewScreen,
    UFOVOnboardingShareScreen,
    UFOVOnboardingSettingsScreen,
    UFOVMenuScreen,
    UFOVMissedItDialog,
    UFOVOnboardingScreen,
    UFOV_ONBOARDING_STATIC_ASSETS,
    UFOV_ONBOARDING_STATIC_ASSET_PATHS,
    UFOVPaywallScreen,
    UFOVPeripheralQuestionScreen,
    UFOVPlayScreen,
    UFOVPostPurchaseScreen,
    UFOVPositionQuestionScreen,
    UFOVConfigScreen,
    UFOVProgressScreen,
    UFOVResultScreen,
} from './UFOVGameUI';
import { UFOVSymbolVariantProvider } from './UFOVSymbol';
import BrainTrainingGames, {
    BRAIN_TRAINING_GAME_MENU_ITEMS,
    BrainTrainingGamesStyles,
    getBrainTrainingGameMenuItems,
    getBrainTrainingGameFromHash,
} from './BrainTrainingGames';
import {
    OnboardingBrainGameTaste,
    OnboardingBrainGameTasteStyles,
    OnboardingTasteStatementScreen,
} from './brain-games/OnboardingBrainGameTaste';

const {
    getUFOVLiveScenarioCatalog,
    getUFOVScenarioRuntimeContext,
    normalizeScenarioId,
} = ufovScenarioCatalog;

const {
    DEFAULT_SYMBOL_VARIANT_ID,
    FRUIT_SYMBOL_VARIANT_ID,
    getUFOVScenarioSymbolVariantId,
    getUFOVSymbolVariant,
    normalizeSymbolVariantId,
    preloadUFOVSymbolAssets,
    preloadUFOVSymbolVariantAssets,
} = ufovSymbolSets;

const {
    SOPT_ESTIMATED_AGE_MAX,
    SOPT_ESTIMATED_AGE_MIN,
    getSoptClassicLevelForEstimatedAge,
    getSoptEstimatedAgeForClassicLevel,
} = soptEstimatedAge;

const {
    createBrainGameLevels,
    normalizeBrainGameLevel,
} = brainGameLevels;

const {
    advanceBrainGameDailyProgress,
    createBrainGameDailyProgress,
    normalizeBrainGameDailyProgress,
} = brainGameDailyProgress;

const {
    createBrainGameHighScores,
    setBrainGameHighScore,
} = brainGameHighScores;

const {
    createGameScoreHistory,
    recordGameScore,
} = gameScoreHistory;

export const UFOV_STORAGE_KEY = UFOV_PROGRESS_STORAGE_KEY;

const getActiveUFOVStorageKey = () => getUFOVProgressStorageKey();

const getConfiguredLegacyProgressStorageKeys = () => {
    const configuredKeys = getUFOVGameConfig().legacyProgressStorageKeys;
    const rawKeys = Array.isArray(configuredKeys)
        ? configuredKeys
        : typeof configuredKeys === 'string'
            ? configuredKeys.split(',')
            : [];

    return rawKeys
        .map((storageKey) => `${storageKey || ''}`.trim())
        .filter(Boolean);
};

const getActiveProgressStorageKeys = () => {
    const activeStorageKey = getActiveUFOVStorageKey();
    const fallbackKeys = activeStorageKey === UFOV_PROGRESS_STORAGE_KEY
        ? UFOV_LEGACY_PROGRESS_STORAGE_KEYS
        : getConfiguredLegacyProgressStorageKeys();

    return [...new Set([activeStorageKey, ...fallbackKeys].filter(Boolean))];
};

const PHASES = {
    MENU: 'menu',
    PRERENDER: 'prerender',
    FIXATION: 'fixation',
    STIMULUS: 'stimulus',
    MASK: 'mask',
    ANSWER_CENTER: 'answer-center',
    ANSWER_POSITION: 'answer-position',
    ANSWER_PERIPHERAL: 'answer-peripheral',
    RESULT: 'result',
    PROGRESS: 'progress',
    CONFIG: 'config',
};
const COUNTDOWN_VIBRATION_DIGITS = [3, 2, 1];
const COUNTDOWN_VIBRATION_MS = 18;
const NATIVE_PURCHASE_WAITING_MS = 12000;
const NATIVE_PURCHASE_LONG_WAITING_MS = 45000;

const POSITIONS = [
    { id: 'north-west', label: 'NW', angle: 225, gridArea: 'nw' },
    { id: 'north', label: 'N', angle: 270, gridArea: 'n' },
    { id: 'north-east', label: 'NE', angle: 315, gridArea: 'ne' },
    { id: 'west', label: 'W', angle: 180, gridArea: 'w' },
    { id: 'east', label: 'E', angle: 0, gridArea: 'e' },
    { id: 'south-west', label: 'SW', angle: 135, gridArea: 'sw' },
    { id: 'south', label: 'S', angle: 90, gridArea: 's' },
    { id: 'south-east', label: 'SE', angle: 45, gridArea: 'se' },
];


const MODE_IDS = {
    CLASSIC: 'classic',
    ADVANCED: 'advanced',
};

const MODE_CONFIGS = {
    [MODE_IDS.CLASSIC]: {
        id: MODE_IDS.CLASSIC,
        label: 'Classic',
        questionCount: 2,
        exposureBonusMs: 0,
    },
    [MODE_IDS.ADVANCED]: {
        id: MODE_IDS.ADVANCED,
        label: 'Advanced',
        questionCount: 3,
        exposureBonusMs: 180,
    },
};

const LEVEL_MIN = 0;
const LEVEL_MAX = 100;
const TUTORIAL_VERSION = 2;
const EXAMPLE_CENTER_ANSWER = '\u2734';
const EXAMPLE_POSITION_ANSWER = 'north-east';
const ONBOARDING_SETTINGS_STEP = 0;
const ONBOARDING_TASTE_STEP = 1;
const ONBOARDING_GAMES_COUNT_STEP = 2;
const ONBOARDING_BELIEF_STEP = 3;
const ONBOARDING_BRAIN_GAMES_EVIDENCE_STEP = 4;
const ONBOARDING_EXCEPTION_STEP = 5;
const ONBOARDING_INTRO_STEP = 6;
const ONBOARDING_EXAMPLE_STEP = 7;
const ONBOARDING_CENTER_STEP = 8;
const ONBOARDING_DIRECTION_STEP = 9;
const ONBOARDING_STUDY_STEP = 10;
const ONBOARDING_RESEARCH_STEP = 11;
const ONBOARDING_PREP_STEP = 12;
const ONBOARDING_AGE_STEP = 13;
const ONBOARDING_SHARE_STEP = 14;
const ONBOARDING_DEMOGRAPHICS_STEP = 15;
const ONBOARDING_IMPROVEMENT_STEP = 16;
const ONBOARDING_CONTACT_STEP = 17;
const ONBOARDING_LIFETIME_STEP = 18;
const ONBOARDING_REVIEW_STEP = 19;
const ONBOARDING_FINAL_STEP = ONBOARDING_REVIEW_STEP;
const ONBOARDING_TOTAL_STEPS = ONBOARDING_FINAL_STEP + 1;
const ONBOARDING_OPTIONAL_STEP_COUNT = 1;
const ONBOARDING_CALIBRATION_ROUND_COUNT = 3;
const ONBOARDING_EVALUATION_ROUND_COUNT = 5;
const ONBOARDING_CALIBRATION_EXPOSURE_BONUS_MS = 240;
const ONBOARDING_EVALUATION_KINDS = {
    CALIBRATION: 'calibration',
    AGE_TEST: 'age-test',
};
const ONBOARDING_ESTIMATED_AGE_MIN = SOPT_ESTIMATED_AGE_MIN;
const ONBOARDING_ESTIMATED_AGE_MAX = SOPT_ESTIMATED_AGE_MAX;
const ONBOARDING_EVALUATION_INITIAL_AGE = 45;
const ONBOARDING_EVALUATION_AGE_JUMP = 8;
const ONBOARDING_EVALUATION_INITIAL_LEVEL = 37;
const ONBOARDING_EVALUATION_ROUNDS = [
    { symbolVariant: 'egg-nest', scenarioId: 'egg-nest' },
    { symbolVariant: 'flower-garden', scenarioId: 'flower-garden' },
    { symbolVariant: 'key-hook-board', scenarioId: 'key-hook-board' },
    { symbolVariant: 'rock-carrier-box', scenarioId: 'rock-carrier-box' },
    { symbolVariant: 'parking-garage', scenarioId: 'parking-garage' },
];
const ONBOARDING_CALIBRATION_ROUNDS = [
    { symbolVariant: 'dog-breed-board', scenarioId: 'dog-breed-board' },
    { symbolVariant: 'leaf-basket', scenarioId: 'leaf-basket' },
    { symbolVariant: 'planes-sky', scenarioId: 'planes-sky' },
];
const ONBOARDING_ANALYTICS_TOTAL_STEPS = 30;
const ONBOARDING_RESUME_VERSION = 1;
const ONBOARDING_RESUME_STORAGE_SUFFIX = 'onboarding-resume.v1';
const ONBOARDING_DEMOGRAPHIC_GENDER_CODES = new Set([
    'woman',
    'man',
    'non_binary',
    'another_identity',
]);
const ONBOARDING_ANALYTICS_STEP_MAP = {
    [ONBOARDING_SETTINGS_STEP]: { step: 'settings', label: 'Language and feedback', stepIndex: 1 },
    [ONBOARDING_TASTE_STEP]: { step: 'brain_game_taste', label: 'Brain game taste', stepIndex: 2 },
    [ONBOARDING_GAMES_COUNT_STEP]: { step: 'brain_game_catalog', label: 'Brain game catalog', stepIndex: 3 },
    [ONBOARDING_BELIEF_STEP]: { step: 'brain_game_belief', label: 'Brain game benefits question', stepIndex: 4 },
    [ONBOARDING_BRAIN_GAMES_EVIDENCE_STEP]: { step: 'brain_game_evidence', label: 'Brain game research answer', stepIndex: 5 },
    [ONBOARDING_EXCEPTION_STEP]: { step: 'sopt_exception', label: 'SOPT evidence exception', stepIndex: 6 },
    [ONBOARDING_INTRO_STEP]: { step: 'intro', label: 'How it works', stepIndex: 7 },
    [ONBOARDING_EXAMPLE_STEP]: { step: 'example_board', label: 'Example board', stepIndex: 8 },
    [ONBOARDING_CENTER_STEP]: { step: 'center_question', label: 'Center question', stepIndex: 9 },
    [ONBOARDING_DIRECTION_STEP]: { step: 'direction_question', label: 'Direction question', stepIndex: 10 },
    [ONBOARDING_STUDY_STEP]: { step: 'study', label: 'Study page', stepIndex: 11 },
    [ONBOARDING_RESEARCH_STEP]: { step: 'research', label: 'Research page', stepIndex: 12 },
    [ONBOARDING_PREP_STEP]: { step: 'calibration_intro', label: 'Calibration introduction', stepIndex: 13 },
    [ONBOARDING_SHARE_STEP]: { step: 'age_share', label: 'Share age estimate', stepIndex: 24 },
    [ONBOARDING_DEMOGRAPHICS_STEP]: { step: 'demographics', label: 'Optional demographics', stepIndex: 25 },
    [ONBOARDING_IMPROVEMENT_STEP]: { step: 'continuous_improvement', label: 'Continuous app improvement', stepIndex: 26 },
    [ONBOARDING_CONTACT_STEP]: { step: 'contact_feedback', label: 'Contact and feedback', stepIndex: 27 },
    [ONBOARDING_LIFETIME_STEP]: { step: 'lifetime_access', label: 'Lifetime access', stepIndex: 28 },
    [ONBOARDING_REVIEW_STEP]: { step: 'review', label: 'Review prompt page', stepIndex: 29 },
};
const POST_PURCHASE_TOTAL_STEPS = 3;
const MENU_EXPLAINER_STEPS = ['classic', 'advanced', 'daily', 'progress'];
const BRAIN_GAMES_HASH = '#/brain-games';
const isBrainTrainingGameMenuId = (gameId) => (
    BRAIN_TRAINING_GAME_MENU_ITEMS.some((game) => game.id === gameId)
);
const getLiveAssetThemeIds = () => (
    getUFOVLiveScenarioCatalog(FRUIT_SYMBOL_VARIANT_ID).map((scenario) => scenario.id)
);
const normalizeSelectedAssetThemeIds = (themeIds) => {
    const liveThemeIds = getLiveAssetThemeIds();
    const selectedThemeIds = Array.isArray(themeIds)
        ? themeIds.map(normalizeScenarioId).filter((themeId) => liveThemeIds.includes(themeId))
        : [];

    return [...new Set(selectedThemeIds.length ? selectedThemeIds : liveThemeIds)];
};
const PAYWALL_PLANS = {
    weekly: {
        id: 'weekly',
        label: 'Weekly',
        note: 'Cancel anytime',
        badge: '',
        ctaLabel: 'Start weekly plan',
        priceFallback: '$1.99 / week',
    },
    lifetime: {
        id: 'lifetime',
        label: 'Lifetime',
        note: 'Pays for itself in 8 weeks',
        badge: 'Forever',
        ctaLabel: 'Get lifetime access',
        priceFallback: '$19.99 once',
    },
};

const formatCopyTemplate = (template, replacements = {}) => Object.entries(replacements).reduce(
    (message, [key, value]) => message.replaceAll(`{${key}}`, value),
    `${template || ''}`
);

const getPaywallPlanCopy = (planId, copy) => {
    const base = PAYWALL_PLANS[planId] || {};

    if (planId === 'weekly') {
        return {
            ...base,
            label: copy.weeklyPlan,
            note: copy.cancelAnytime,
            badge: '',
            ctaLabel: copy.startWeeklyPlan,
            priceFallback: `$1.99 ${copy.weekSuffix}`,
        };
    }

    if (planId === 'lifetime') {
        return {
            ...base,
            label: copy.lifetimePlan,
            note: copy.oneTimePurchase,
            badge: copy.bestValue,
            ctaLabel: copy.getLifetimeAccess,
            priceFallback: `$19.99 ${copy.onceSuffix}`,
        };
    }

    return base;
};

const getLocalDayKey = (date = new Date()) => {
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${date.getFullYear()}-${month}-${day}`;
};

const createDailyProgress = () => ({
    date: getLocalDayKey(),
    runs: 0,
    seconds: 0,
    modes: {
        [MODE_IDS.CLASSIC]: { runs: 0, seconds: 0 },
        [MODE_IDS.ADVANCED]: { runs: 0, seconds: 0 },
    },
});

const createStreakProgress = () => ({
    currentDays: 0,
    bestDays: 0,
    lastDay: null,
});

const createModeProgress = () => ({
    level: 0,
    bestLevel: 0,
    lastLevelDelta: 0,
    trials: 0,
    perfectTrials: 0,
    centerHits: 0,
    positionHits: 0,
    peripheralHits: 0,
    lastPlayedAt: null,
});

const createEvaluationRoundProgress = (level) => ({
    ...createModeProgress(),
    level,
    bestLevel: level,
});

const createOnboardingEvaluationState = () => ({
    active: false,
    kind: ONBOARDING_EVALUATION_KINDS.CALIBRATION,
    roundIndex: 0,
    rounds: [],
    calibrationRounds: [],
    calibratedLevel: ONBOARDING_EVALUATION_INITIAL_LEVEL,
    progress: createEvaluationRoundProgress(ONBOARDING_EVALUATION_INITIAL_LEVEL),
    estimatedAge: null,
});

const getOnboardingSkippedStepCountBefore = (
    stepIndex,
    showStudyPage = true,
    showResearchPage = true,
    showEvidenceExceptionPage = true,
    showReviewPage = false
) => {
    let skippedCount = 0;

    if (!showEvidenceExceptionPage && stepIndex > ONBOARDING_EXCEPTION_STEP) {
        skippedCount += ONBOARDING_OPTIONAL_STEP_COUNT;
    }

    if (!showStudyPage && stepIndex > ONBOARDING_STUDY_STEP) {
        skippedCount += ONBOARDING_OPTIONAL_STEP_COUNT;
    }

    if (!showResearchPage && stepIndex > ONBOARDING_RESEARCH_STEP) {
        skippedCount += ONBOARDING_OPTIONAL_STEP_COUNT;
    }

    if (!showReviewPage && stepIndex > ONBOARDING_REVIEW_STEP) {
        skippedCount += ONBOARDING_OPTIONAL_STEP_COUNT;
    }

    return skippedCount;
};
const getOnboardingVisibleStepIndex = (
    stepIndex,
    showStudyPage = true,
    showResearchPage = true,
    showEvidenceExceptionPage = true,
    showReviewPage = false
) => (
    stepIndex - getOnboardingSkippedStepCountBefore(
        stepIndex,
        showStudyPage,
        showResearchPage,
        showEvidenceExceptionPage,
        showReviewPage
    )
);
const getOnboardingTotalSteps = (
    showStudyPage = true,
    showResearchPage = true,
    showEvidenceExceptionPage = true,
    showReviewPage = false
) => (
    ONBOARDING_TOTAL_STEPS
    - (showStudyPage ? 0 : ONBOARDING_OPTIONAL_STEP_COUNT)
    - (showResearchPage ? 0 : ONBOARDING_OPTIONAL_STEP_COUNT)
    - (showEvidenceExceptionPage ? 0 : ONBOARDING_OPTIONAL_STEP_COUNT)
    - (showReviewPage ? 0 : ONBOARDING_OPTIONAL_STEP_COUNT)
);
const getOnboardingProgressLabel = (
    stepIndex,
    showStudyPage = true,
    showResearchPage = true,
    showEvidenceExceptionPage = true,
    showReviewPage = false
) => (
    `${getOnboardingVisibleStepIndex(stepIndex, showStudyPage, showResearchPage, showEvidenceExceptionPage, showReviewPage) + 1} / ${getOnboardingTotalSteps(showStudyPage, showResearchPage, showEvidenceExceptionPage, showReviewPage)}`
);
const isConfigFlagEnabled = (value, fallback = true) => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (value === null || value === undefined || value === '') {
        return fallback;
    }

    return ['1', 'true', 'yes', 'on'].includes(`${value}`.trim().toLowerCase());
};
const normalizeLanguage = (language) => normalizeUFOVLanguage(language);
const getOnboardingEvaluationRounds = (kind) => (
    kind === ONBOARDING_EVALUATION_KINDS.CALIBRATION
        ? ONBOARDING_CALIBRATION_ROUNDS
        : ONBOARDING_EVALUATION_ROUNDS
);
const getOnboardingEvaluationRoundCount = (kind) => (
    kind === ONBOARDING_EVALUATION_KINDS.CALIBRATION
        ? ONBOARDING_CALIBRATION_ROUND_COUNT
        : ONBOARDING_EVALUATION_ROUND_COUNT
);
const getOnboardingEvaluationRoundConfig = (roundIndex, kind) => {
    const rounds = getOnboardingEvaluationRounds(kind);

    return rounds[roundIndex]
        || rounds[rounds.length - 1]
        || { symbolVariant: DEFAULT_SYMBOL_VARIANT_ID, scenarioId: DEFAULT_SYMBOL_VARIANT_ID };
};
const getOnboardingEvaluationWarmupVariantId = (roundConfig) => (
    roundConfig.symbolVariant === FRUIT_SYMBOL_VARIANT_ID
        ? getUFOVScenarioSymbolVariantId(FRUIT_SYMBOL_VARIANT_ID, roundConfig.scenarioId)
        : roundConfig.symbolVariant
);
const getOnboardingEvaluationAnalyticsStep = (roundIndex, kind) => {
    const isCalibration = kind === ONBOARDING_EVALUATION_KINDS.CALIBRATION;
    const roundCount = getOnboardingEvaluationRoundCount(kind);
    const safeRoundIndex = clamp(Math.round(Number(roundIndex) || 0), 0, roundCount - 1);
    const roundNumber = safeRoundIndex + 1;

    return {
        step: `${isCalibration ? 'calibration' : 'age_test'}_round_${roundNumber}`,
        label: `${isCalibration ? 'Calibration' : 'Age test'} round ${roundNumber}`,
        stepIndex: (isCalibration ? 13 : 17) + roundNumber,
    };
};

const isTruthyUrlFlag = (value) => ['1', 'true', 'yes', 'on'].includes(
    `${value || ''}`.trim().toLowerCase()
);

const readUFOVDebugConfigFromUrl = () => {
    if (typeof window === 'undefined') {
        return {
            baseVariantId: '',
            trialVariantId: '',
            scenarioId: '',
            modeId: '',
            level: null,
            autostart: false,
        };
    }

    try {
        const searchParams = new URLSearchParams(window.location.search);
        const rawVariantValue = `${searchParams.get('ufovVariant') || searchParams.get('variant') || searchParams.get('symbolVariant') || ''}`.trim();
        const rawScenarioId = normalizeScenarioId(
            searchParams.get('ufovScenario') || searchParams.get('scenario') || searchParams.get('scenarioId') || ''
        );
        const rawModeId = `${searchParams.get('ufovMode') || searchParams.get('mode') || ''}`
            .trim()
            .toLowerCase();
        const rawLevelValue = `${searchParams.get('ufovLevel') || searchParams.get('level') || ''}`.trim();
        const normalizedVariantId = rawVariantValue ? normalizeSymbolVariantId(rawVariantValue) : '';
        const directVariantIdFromVariant = (
            normalizedVariantId
            && normalizedVariantId !== FRUIT_SYMBOL_VARIANT_ID
        )
            ? normalizedVariantId
            : '';
        const directVariantIdFromScenario = (
            rawScenarioId
            && normalizeSymbolVariantId(rawScenarioId) === rawScenarioId
            && rawScenarioId !== FRUIT_SYMBOL_VARIANT_ID
        )
            ? rawScenarioId
            : '';
        const inferredBaseVariantId = directVariantIdFromVariant
            ? FRUIT_SYMBOL_VARIANT_ID
            : normalizedVariantId || (rawScenarioId ? FRUIT_SYMBOL_VARIANT_ID : '');
        const modeId = MODE_CONFIGS[rawModeId] ? rawModeId : '';
        const hasExplicitLevel = rawLevelValue !== '';
        const parsedLevel = hasExplicitLevel
            ? clamp(Number(rawLevelValue) || 0, LEVEL_MIN, LEVEL_MAX)
            : null;

        return {
            baseVariantId: inferredBaseVariantId,
            trialVariantId: directVariantIdFromVariant || directVariantIdFromScenario,
            scenarioId: rawScenarioId || directVariantIdFromVariant,
            modeId,
            level: parsedLevel,
            autostart: isTruthyUrlFlag(
                searchParams.get('ufovStart')
                || searchParams.get('start')
                || searchParams.get('autostart')
            ),
        };
    } catch (error) {
        return {
            baseVariantId: '',
            trialVariantId: '',
            scenarioId: '',
            modeId: '',
            level: null,
            autostart: false,
        };
    }
};

const shouldForceOnboardingFromUrl = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    try {
        const searchParams = new URLSearchParams(window.location.search);
        const flagValue = `${searchParams.get('onboarding') || searchParams.get('tutorial') || ''}`
            .trim()
            .toLowerCase();

        return /^\/(?:test\/)?onboarding(?:\/|$)/i.test(window.location.pathname || '')
            || ['1', 'true', 'yes'].includes(flagValue);
    } catch (error) {
        return false;
    }
};

const isTestOnboardingRoute = () => (
    typeof window !== 'undefined'
    && /^\/test\/onboarding(?:\/|$)/i.test(window.location.pathname || '')
);

const isOnboardingPreviewOrDebugContext = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    const pathname = `${window.location.pathname || ''}`.toLowerCase();

    if (
        /^\/test(?:\/|$)/.test(pathname)
        || /^\/(?:admin(?:\/|_|$)|videos?(?:\/|$)|video-render(?:\/|$)|_local(?:\/|$)|preview(?:\/|$))/.test(pathname)
    ) {
        return true;
    }

    try {
        const searchParams = new URLSearchParams(window.location.search);

        return [
            'qa',
            'debug',
            'preview',
            'postpurchase',
            'postPurchase',
            'post_purchase',
            'postpurchaseStep',
            'postPurchaseStep',
            'post_purchase_step',
        ].some((parameter) => searchParams.has(parameter));
    } catch (error) {
        return false;
    }
};

const TEST_ONBOARDING_PAGES = [
    ['settings', 'Language', ONBOARDING_SETTINGS_STEP],
    ['taste', 'Game taste', ONBOARDING_TASTE_STEP],
    ['games', 'Brain game catalog', ONBOARDING_GAMES_COUNT_STEP],
    ['belief', 'Benefits question', ONBOARDING_BELIEF_STEP],
    ['evidence', 'Research answer', ONBOARDING_BRAIN_GAMES_EVIDENCE_STEP],
    ['exception', 'SOPT exception', ONBOARDING_EXCEPTION_STEP],
    ['intro', 'How it works', ONBOARDING_INTRO_STEP],
    ['example', 'Example board', ONBOARDING_EXAMPLE_STEP],
    ['center', 'Center question', ONBOARDING_CENTER_STEP],
    ['direction', 'Direction question', ONBOARDING_DIRECTION_STEP],
    ['study', 'Study', ONBOARDING_STUDY_STEP],
    ['research', 'Research', ONBOARDING_RESEARCH_STEP],
    ['prep', 'Calibration intro', ONBOARDING_PREP_STEP],
    ['evaluation', 'Calibration game', 'evaluation'],
    ['age', 'Age test choice', ONBOARDING_AGE_STEP],
    ['share', 'Share age', ONBOARDING_SHARE_STEP],
    ['demographics', 'Age and gender', ONBOARDING_DEMOGRAPHICS_STEP],
    ['improvement', 'App improvement', ONBOARDING_IMPROVEMENT_STEP],
    ['contact', 'Contact and feedback', ONBOARDING_CONTACT_STEP],
    ['lifetime', 'Lifetime access', ONBOARDING_LIFETIME_STEP],
    ['review', 'Review', ONBOARDING_REVIEW_STEP],
    ['paywall', 'Paywall', 'paywall'],
    ['menu', 'Menu', 'menu'],
];

const TEST_ONBOARDING_STYLES = `
    .UFOV-test-nav { position: fixed; right: 12px; top: 12px; z-index: 10000; font-family: inherit; }
    .UFOV-test-nav__toggle { background: #111827; border: 2px solid #fff; border-radius: 999px; box-shadow: 0 8px 24px rgba(15,23,42,.28); color: #fff; cursor: pointer; font-size: 12px; font-weight: 700; padding: 9px 12px; }
    .UFOV-test-nav__panel { background: rgba(255,255,255,.98); border: 1px solid #dbe3f0; border-radius: 16px; box-shadow: 0 18px 48px rgba(15,23,42,.24); display: none; margin-top: 7px; max-height: calc(100vh - 70px); overflow: auto; padding: 10px; width: min(230px, calc(100vw - 24px)); }
    .UFOV-test-nav.is-open .UFOV-test-nav__panel { display: grid; gap: 6px; }
    .UFOV-test-nav__title { color: #64748b; font-size: 11px; font-weight: 700; letter-spacing: .08em; padding: 3px 5px 6px; text-transform: uppercase; }
    .UFOV-test-nav__title:not(:first-child) { border-top: 1px solid #e2e8f0; margin-top: 5px; padding-top: 11px; }
    .UFOV-test-nav__page { background: #f1f5f9; border: 0; border-radius: 10px; color: #172033; cursor: pointer; font: inherit; font-size: 13px; font-weight: 700; padding: 9px 10px; text-align: left; }
    .UFOV-test-nav__page.is-active { background: #13213b; color: #fff; }
`;

const readPostPurchasePreviewStepFromUrl = () => {
    if (typeof window === 'undefined') {
        return -1;
    }

    try {
        const searchParams = new URLSearchParams(window.location.search);
        const rawFlagValue = (
            searchParams.get('postpurchase')
            || searchParams.get('postPurchase')
            || searchParams.get('post_purchase')
            || ''
        ).trim();
        const rawStepValue = (
            searchParams.get('postpurchaseStep')
            || searchParams.get('postPurchaseStep')
            || searchParams.get('post_purchase_step')
            || ''
        ).trim();
        const parsedStep = Number.parseInt(rawStepValue || rawFlagValue, 10);
        const hasStepValue = Number.isFinite(parsedStep)
            && parsedStep >= 1
            && parsedStep <= POST_PURCHASE_TOTAL_STEPS;
        const shouldPreview = isTruthyUrlFlag(rawFlagValue) || hasStepValue;

        if (!shouldPreview) {
            return -1;
        }

        return hasStepValue ? parsedStep - 1 : 0;
    } catch (error) {
        return -1;
    }
};

const createDefaultProgress = () => ({
    activeMode: MODE_IDS.CLASSIC,
    modes: {
        [MODE_IDS.CLASSIC]: createModeProgress(),
        [MODE_IDS.ADVANCED]: createModeProgress(),
    },
    brainGameLevels: createBrainGameLevels(),
    brainGameDaily: createBrainGameDailyProgress(),
    brainGameHighScores: createBrainGameHighScores(),
    gameScoreHistory: createGameScoreHistory(),
    daily: createDailyProgress(),
    estimatedAge: null,
    streak: createStreakProgress(),
    tutorialSeen: false,
    tutorialVersion: TUTORIAL_VERSION,
    menuExplainerSeen: false,
    symbolVariantMode: 'assets',
    selectedAssetThemeIds: getLiveAssetThemeIds(),
    countdownHapticsEnabled: true,
    colorBlindModeEnabled: false,
    language: detectPreferredUFOVLanguage(),
    languageManuallySelected: false,
    level: 0,
    bestLevel: 0,
    lastLevelDelta: 0,
    trials: 0,
    perfectTrials: 0,
    centerHits: 0,
    positionHits: 0,
    peripheralHits: 0,
    lastPlayedAt: null,
});

const INPUT_KEYS = {
    cancel: ['Escape'],
    confirm: ['Enter', ' '],
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const randomItem = (items) => items[Math.floor(Math.random() * items.length)];

const getErrorMessage = (error, fallback) => {
    if (typeof error === 'string' && error.trim()) {
        return error.trim();
    }

    if (error instanceof Error && error.message) {
        return error.message.trim();
    }

    if (error && typeof error === 'object' && typeof error.message === 'string') {
        return error.message.trim();
    }

    return fallback;
};

const isBillingConfigurationMessage = (message) => (
    /revenuecat|configuration|configured|offering|offerings-empty|storekit|app store connect|api key|product|package|sdk/i
        .test(`${message || ''}`)
);

const getBillingErrorMessage = (error, fallback) => {
    const message = getErrorMessage(error, fallback);

    if (isBillingConfigurationMessage(message)) {
        console.warn('[RevenueCat billing diagnostic]', message);
        return fallback;
    }

    if (message.length > 180) {
        console.warn('[Billing diagnostic]', message);
        return fallback;
    }

    return message || fallback;
};

const isCancelledError = (error) => {
    const message = getErrorMessage(error, '');
    const errorCode = error?.errorCode ?? error?.code;

    return Number(errorCode) === 1 || /cancel/i.test(message);
};

const readWebFormattedPrice = (rcPackage) => (
    rcPackage?.webBillingProduct?.price?.formattedPrice
    || rcPackage?.webBillingProduct?.currentPrice?.formattedPrice
    || ''
);

const readWebCurrencyCode = (rcPackage) => (
    rcPackage?.webBillingProduct?.price?.currencyCode
    || rcPackage?.webBillingProduct?.price?.currency
    || rcPackage?.webBillingProduct?.price?.currency_code
    || rcPackage?.webBillingProduct?.currentPrice?.currencyCode
    || rcPackage?.webBillingProduct?.currentPrice?.currency
    || rcPackage?.webBillingProduct?.currentPrice?.currency_code
    || rcPackage?.currencyCode
    || rcPackage?.currency_code
    || ''
);

const readWebPriceValue = (rcPackage) => {
    const price = rcPackage?.webBillingProduct?.price || rcPackage?.webBillingProduct?.currentPrice;

    if (Number.isFinite(price?.amountMicros)) {
        return price.amountMicros / 1000000;
    }

    if (Number.isFinite(price?.amount)) {
        return price.amount / 100;
    }

    return null;
};

const getBrowserCurrencyCodeFallback = () => {
    if (typeof Intl === 'undefined') {
        return 'USD';
    }

    try {
        const timeZone = `${Intl.DateTimeFormat().resolvedOptions().timeZone || ''}`;

        if (timeZone.startsWith('Europe/')) {
            return 'EUR';
        }
    } catch (error) {
        // Use USD when browser locale data is unavailable.
    }

    return 'USD';
};

const getCurrencySymbol = (currencyCode = '') => {
    const normalizedCurrencyCode = `${currencyCode || getBrowserCurrencyCodeFallback()}`.trim().toUpperCase();

    if (normalizedCurrencyCode === 'EUR') {
        return '\u20ac';
    }

    if (['GBP', 'USD', 'CAD', 'AUD', 'NZD'].includes(normalizedCurrencyCode)) {
        return normalizedCurrencyCode === 'GBP' ? '\u00a3' : '$';
    }

    if (normalizedCurrencyCode === 'JPY' || normalizedCurrencyCode === 'CNY') {
        return '\u00a5';
    }

    return normalizedCurrencyCode ? `${normalizedCurrencyCode} ` : '$';
};

const ensureCurrencySymbol = (formattedPrice = '', currencyCode = '') => {
    const trimmedPrice = `${formattedPrice || ''}`.trim();

    if (!trimmedPrice) {
        return '';
    }

    if (/[$\u20ac\u00a3\u00a5]|[A-Z]{3}/i.test(trimmedPrice)) {
        return trimmedPrice;
    }

    return `${getCurrencySymbol(currencyCode)}${trimmedPrice}`;
};

const readNumericPriceFromText = (value = '') => {
    const match = `${value || ''}`.match(/\d[\d.,\s]*/);

    if (!match) {
        return null;
    }

    const compact = match[0].replace(/\s/g, '');
    const lastComma = compact.lastIndexOf(',');
    const lastDot = compact.lastIndexOf('.');
    const decimalSeparator = lastComma > lastDot ? ',' : '.';
    const normalized = compact
        .replace(decimalSeparator === ',' ? /\./g : /,/g, '')
        .replace(',', '.');
    const parsed = Number(normalized);

    return Number.isFinite(parsed) ? parsed : null;
};

const inferCurrencyCodeFromText = (value = '') => {
    const text = `${value || ''}`.toUpperCase();

    if (text.includes('EUR') || text.includes('\u20ac')) {
        return 'EUR';
    }

    if (text.includes('GBP') || text.includes('\u00a3')) {
        return 'GBP';
    }

    if (text.includes('JPY')) {
        return 'JPY';
    }

    if (text.includes('$')) {
        return 'USD';
    }

    return '';
};

const hasPackageAliasToken = (plan, pattern) => [
    plan.id,
    plan.packageId,
    plan.packageIdentifier,
    plan.nativePackageId,
    plan.nativePackageIdentifier,
    ...(Array.isArray(plan.packageIdentifiers) ? plan.packageIdentifiers : []),
    ...(Array.isArray(plan.nativePackageIdentifiers) ? plan.nativePackageIdentifiers : []),
].some((value) => pattern.test(`${value || ''}`));

const getPackageBackedPriceLine = (plan, planKind, copy) => {
    if (planKind === 'weekly') {
        if (hasPackageAliasToken(plan, /(^|[_:-])799($|[_:-])|weekly[-_:]?799|weekly_3/i)) {
            return `${getCurrencySymbol()}7.99 ${copy.weekSuffix}`;
        }

        if (hasPackageAliasToken(plan, /(^|[_:-])199($|[_:-])|weekly[-_:]?199|weekly_2/i)) {
            return `${getCurrencySymbol()}1.99 ${copy.weekSuffix}`;
        }

        if (hasPackageAliasToken(plan, /(^|[_:-])099($|[_:-])|weekly[-_:]?099|weekly_1/i)) {
            return `${getCurrencySymbol()}0.99 ${copy.weekSuffix}`;
        }
    }

    if (planKind === 'lifetime') {
        if (hasPackageAliasToken(plan, /1999|lifetime_2/i)) {
            return `${getCurrencySymbol()}19.99 ${copy.onceSuffix}`;
        }

        if (hasPackageAliasToken(plan, /799|lifetime_1/i)) {
            return `${getCurrencySymbol()}7.99 ${copy.onceSuffix}`;
        }
    }

    return '';
};

const findNativePackageOption = (nativePackageOptions, planId) => (
    nativePackageOptions.find((item) => (
        item.id === planId
        || item.identifier === planId
        || item.productIdentifier === planId
    )) || null
);

const getNativePackageLabel = (nativePackage, copy = getUFOVGameCopy()) => {
    const planKind = getNativePackagePlanKind(nativePackage);

    if (planKind === 'weekly') {
        return copy.weeklyPlan;
    }

    if (planKind === 'lifetime') {
        return copy.lifetimePlan;
    }

    return nativePackage.productTitle || copy.premium;
};

const getPaywallPlanFallbacks = (copy = getUFOVGameCopy()) => ({
    weekly: getPaywallPlanCopy('weekly', copy),
    lifetime: getPaywallPlanCopy('lifetime', copy),
});

const resolveNativePackageForPlan = (nativePackageOptions, plan) => {
    const nativeAliases = plan.nativePackageIdentifiers?.length
        ? plan.nativePackageIdentifiers
        : plan.packageIdentifiers;
    const nativeAliasMatch = nativeAliases.reduce((matchedPackage, alias) => (
        matchedPackage
        || nativePackageOptions.find((item) => matchesPackageAliases(item, [alias]))
        || null
    ), null);

    if (nativeAliasMatch || plan.nativePackageIdentifiers?.length) {
        return nativeAliasMatch || null;
    }

    return nativePackageOptions.find((item) => (
        item.id === plan.id
        || item.identifier === plan.id
        || item.productIdentifier === plan.id
    )) || null;
};

const createConfiguredPlanView = ({
    plan,
    copy = getUFOVGameCopy(),
    isNative = false,
    nativePackage = null,
    rcPackage = null,
    billingConfig = {},
}) => {
    const planKind = plan.planKind || (nativePackage ? getNativePackagePlanKind(nativePackage) : plan.id);
    const fallback = getPaywallPlanCopy(planKind === 'lifetime' ? 'lifetime' : 'weekly', copy);
    const formattedPrice = isNative
        ? nativePackage?.formattedPrice || ''
        : readWebFormattedPrice(rcPackage);
    const currencyCode = isNative
        ? nativePackage?.currencyCode || ''
        : readWebCurrencyCode(rcPackage);
    const normalizedFormattedPrice = ensureCurrencySymbol(formattedPrice, currencyCode);
    const numericPrice = isNative
        ? (Number.isFinite(nativePackage?.price) ? nativePackage.price : null)
        : readWebPriceValue(rcPackage);
    const suffix = planKind === 'weekly'
        ? copy.weekSuffix
        : planKind === 'lifetime'
            ? copy.onceSuffix
            : '';
    const priceLine = normalizedFormattedPrice
        ? `${normalizedFormattedPrice}${suffix ? ` ${suffix}` : ''}`
        : getPackageBackedPriceLine(plan, planKind, copy) || plan.priceLine || plan.priceFallback || fallback.priceFallback;
    let comparisonPrice = '';

    const effectiveNumericPrice = Number.isFinite(numericPrice)
        ? numericPrice
        : readNumericPriceFromText(normalizedFormattedPrice || plan.priceLine || plan.priceFallback);
    const comparisonCurrencyCode = currencyCode
        || inferCurrencyCodeFromText(normalizedFormattedPrice || plan.priceLine || plan.priceFallback)
        || getBrowserCurrencyCodeFallback();

    if (planKind === 'lifetime' && Number.isFinite(effectiveNumericPrice) && effectiveNumericPrice > 0) {
        try {
            comparisonPrice = new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency: `${comparisonCurrencyCode}`.toUpperCase(),
            }).format(effectiveNumericPrice * 2);
        } catch (error) {
            comparisonPrice = `${getCurrencySymbol(comparisonCurrencyCode)}${(effectiveNumericPrice * 2).toFixed(2)}`;
        }
    }

    return {
        ...fallback,
        ...plan,
        label: plan.label || (nativePackage ? getNativePackageLabel(nativePackage, copy) : fallback.label),
        note: plan.note || fallback.note || (planKind === 'premium' ? copy.storeCheckout : ''),
        badge: plan.badge || '',
        ctaLabel: plan.ctaLabel || fallback.ctaLabel || copy.continueLabel,
        priceLine,
        comparisonPrice,
        discountLabel: planKind === 'lifetime' ? '50% OFF' : '',
        nativePackage,
        rcPackage,
        planKind,
        ready: isNative
            ? Boolean(nativePackage || plan.nativePackageIdentifiers?.length || plan.packageIdentifiers?.length)
            : Boolean(rcPackage) || Boolean(billingConfig.purchaseLink),
    };
};

const isLiveTrialPhase = (phase) => (
    phase === PHASES.PRERENDER
    || phase === PHASES.FIXATION
    || phase === PHASES.STIMULUS
    || phase === PHASES.MASK
);

const waitForUFOVImageDecode = (image) => {
    const waitForLoad = image.complete && image.naturalWidth > 0
        ? Promise.resolve()
        : new Promise((resolve) => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
        });

    return waitForLoad.then(() => (
        typeof image.decode === 'function'
            ? image.decode().catch(() => undefined)
            : undefined
    ));
};

const waitForQuestionPrewarmLayers = () => {
    if (typeof document === 'undefined') {
        return Promise.resolve();
    }

    const images = Array.from(document.querySelectorAll('[data-ufov-question-prewarm="true"] img'));

    if (!images.length) {
        return Promise.resolve();
    }

    return Promise.all(images.map(waitForUFOVImageDecode)).then(() => undefined);
};

const preloadStaticImagePath = (path) => {
    if (!path || typeof window === 'undefined' || typeof window.Image !== 'function') {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        const image = new window.Image();
        const finish = () => {
            if (typeof image.decode === 'function') {
                image.decode().catch(() => undefined).then(resolve);
                return;
            }

            resolve();
        };

        image.decoding = 'async';
        image.loading = 'eager';
        image.fetchPriority = 'high';
        image.onload = finish;
        image.onerror = resolve;
        image.src = path;

        if (image.complete) {
            finish();
        }
    });
};

const preloadStaticOnboardingAssets = ({ includeStudyPage = true, includeResearchPage = true } = {}) => {
    const staticAssetPaths = UFOV_ONBOARDING_STATIC_ASSET_PATHS.filter((path) => (
        (includeStudyPage || path !== UFOV_ONBOARDING_STATIC_ASSETS.studyPage)
        && (includeResearchPage || path !== UFOV_ONBOARDING_STATIC_ASSETS.researchPage)
    ));

    return Promise.all(
        staticAssetPaths.map(preloadStaticImagePath)
    ).then(() => undefined);
};

const normalizeModeProgress = (rawProgress = {}) => {
    const merged = {
        ...createModeProgress(),
        ...rawProgress,
    };
    const level = clamp(Number(merged.level) || 0, LEVEL_MIN, LEVEL_MAX);
    const bestLevel = clamp(Number(merged.bestLevel) || level, LEVEL_MIN, LEVEL_MAX);

    return {
        ...createModeProgress(),
        level,
        bestLevel: Math.max(bestLevel, level),
        lastLevelDelta: Number(merged.lastLevelDelta) || 0,
        trials: Number(merged.trials) || 0,
        perfectTrials: Number(merged.perfectTrials) || 0,
        centerHits: Number(merged.centerHits) || 0,
        positionHits: Number(merged.positionHits) || 0,
        peripheralHits: Number(merged.peripheralHits) || 0,
        lastPlayedAt: merged.lastPlayedAt || null,
    };
};

const normalizeDailyProgress = (rawDailyProgress = {}) => {
    const today = getLocalDayKey();

    if (rawDailyProgress.date !== today) {
        return createDailyProgress();
    }

    const runs = Math.max(0, Number(rawDailyProgress.runs) || 0);
    const seconds = Math.max(0, Number(rawDailyProgress.seconds) || 0);
    const hasModeProgress = rawDailyProgress.modes && typeof rawDailyProgress.modes === 'object';
    const normalizeModeDaily = (modeId) => ({
        runs: Math.max(
            0,
            Number(rawDailyProgress.modes?.[modeId]?.runs)
                || (!hasModeProgress && modeId === MODE_IDS.CLASSIC ? runs : 0)
        ),
        seconds: Math.max(
            0,
            Number(rawDailyProgress.modes?.[modeId]?.seconds)
                || (!hasModeProgress && modeId === MODE_IDS.CLASSIC ? seconds : 0)
        ),
    });

    return {
        date: today,
        runs,
        seconds,
        modes: {
            [MODE_IDS.CLASSIC]: normalizeModeDaily(MODE_IDS.CLASSIC),
            [MODE_IDS.ADVANCED]: normalizeModeDaily(MODE_IDS.ADVANCED),
        },
    };
};

const getDayDistance = (fromDay, toDay) => {
    const fromTime = Date.parse(`${fromDay}T00:00:00`);
    const toTime = Date.parse(`${toDay}T00:00:00`);

    if (!Number.isFinite(fromTime) || !Number.isFinite(toTime)) {
        return Number.POSITIVE_INFINITY;
    }

    return Math.round((toTime - fromTime) / 86400000);
};

const normalizeStreakProgress = (rawStreak = {}) => ({
    currentDays: Math.max(0, Number(rawStreak.currentDays) || 0),
    bestDays: Math.max(0, Number(rawStreak.bestDays) || 0),
    lastDay: typeof rawStreak.lastDay === 'string' ? rawStreak.lastDay : null,
});

const advanceStreakProgress = (rawStreak = {}) => {
    const streak = normalizeStreakProgress(rawStreak);
    const today = getLocalDayKey();

    if (streak.lastDay === today) {
        return streak;
    }

    const continued = streak.lastDay && getDayDistance(streak.lastDay, today) === 1;
    const currentDays = continued ? streak.currentDays + 1 : 1;

    return {
        currentDays,
        bestDays: Math.max(streak.bestDays, currentDays),
        lastDay: today,
    };
};

const advanceDailyProgress = (dailyProgress, startedAt, modeId = MODE_IDS.CLASSIC) => {
    const currentDailyProgress = normalizeDailyProgress(dailyProgress);
    const normalizedModeId = MODE_CONFIGS[modeId]?.id || MODE_IDS.CLASSIC;
    const elapsedSeconds = startedAt
        ? clamp(Math.round((Date.now() - startedAt) / 1000), 4, 600)
        : 0;
    const currentModeDaily = currentDailyProgress.modes[normalizedModeId] || { runs: 0, seconds: 0 };

    return {
        ...currentDailyProgress,
        runs: currentDailyProgress.runs + 1,
        seconds: currentDailyProgress.seconds + elapsedSeconds,
        modes: {
            ...currentDailyProgress.modes,
            [normalizedModeId]: {
                runs: currentModeDaily.runs + 1,
                seconds: currentModeDaily.seconds + elapsedSeconds,
            },
        },
    };
};

const getOnboardingRoundScore = (centerCorrect, positionCorrect) => (
    (Number(Boolean(centerCorrect)) + Number(Boolean(positionCorrect))) / 2
);

const normalizeOnboardingEvaluationLevel = (level, fallback = ONBOARDING_EVALUATION_INITIAL_LEVEL) => {
    const parsedLevel = Number(level);

    return clamp(
        Number.isFinite(parsedLevel) ? parsedLevel : fallback,
        LEVEL_MIN,
        LEVEL_MAX
    );
};

const getOnboardingEvaluationAgeAdjustment = (score) => {
    const safeScore = clamp(Number(score) || 0, 0, 1);

    return (0.5 - safeScore) * ONBOARDING_EVALUATION_AGE_JUMP * 2;
};

const getNextOnboardingEvaluationLevel = (currentLevel, score) => {
    const level = normalizeOnboardingEvaluationLevel(currentLevel);
    const currentAge = getSoptEstimatedAgeForClassicLevel(level);
    const nextAge = clamp(
        currentAge + getOnboardingEvaluationAgeAdjustment(score),
        ONBOARDING_ESTIMATED_AGE_MIN,
        ONBOARDING_ESTIMATED_AGE_MAX
    );

    return getSoptClassicLevelForEstimatedAge(nextAge);
};

const getClassicLevelFromOnboardingEvaluationLevel = (level) => {
    const evaluatedLevel = normalizeOnboardingEvaluationLevel(level);
    const adjustedLevel = evaluatedLevel > 40 ? evaluatedLevel - 10 : evaluatedLevel;

    return clamp(adjustedLevel, LEVEL_MIN, LEVEL_MAX);
};

const advanceOnboardingEvaluationProgress = (progress, answers) => {
    const centerCorrect = Boolean(answers.centerCorrect);
    const positionCorrect = Boolean(answers.positionCorrect);
    const score = getOnboardingRoundScore(centerCorrect, positionCorrect);
    const previousLevel = normalizeOnboardingEvaluationLevel(progress?.level);
    const nextLevel = getNextOnboardingEvaluationLevel(previousLevel, score);
    const perfect = centerCorrect && positionCorrect;

    return {
        ...createEvaluationRoundProgress(previousLevel),
        ...(progress || {}),
        level: nextLevel,
        bestLevel: Math.max(progress?.bestLevel || previousLevel, previousLevel, nextLevel),
        lastLevelDelta: nextLevel - previousLevel,
        trials: (progress?.trials || 0) + 1,
        perfectTrials: (progress?.perfectTrials || 0) + (perfect ? 1 : 0),
        centerHits: (progress?.centerHits || 0) + (centerCorrect ? 1 : 0),
        positionHits: (progress?.positionHits || 0) + (positionCorrect ? 1 : 0),
        lastPlayedAt: new Date().toISOString(),
    };
};

const getOnboardingEstimatedAge = (rounds = []) => {
    if (!rounds.length) {
        return ONBOARDING_EVALUATION_INITIAL_AGE;
    }

    if (rounds.every((round) => Number(round.score) >= 1)) {
        return ONBOARDING_ESTIMATED_AGE_MIN;
    }

    if (rounds.every((round) => Number(round.score) <= 0)) {
        return ONBOARDING_ESTIMATED_AGE_MAX;
    }

    const weightedEstimatedAge = rounds.reduce((total, round, index) => {
        const score = clamp(Number(round.score) || 0, 0, 1);
        const level = normalizeOnboardingEvaluationLevel(round.level, LEVEL_MIN);
        const adjustedAge = clamp(
            getSoptEstimatedAgeForClassicLevel(level) + getOnboardingEvaluationAgeAdjustment(score),
            ONBOARDING_ESTIMATED_AGE_MIN,
            ONBOARDING_ESTIMATED_AGE_MAX
        );

        return total + (adjustedAge * (index + 1));
    }, 0);
    const weightedMax = rounds.reduce((total, _round, index) => total + index + 1, 0);
    const averageEstimatedAge = weightedMax > 0
        ? weightedEstimatedAge / weightedMax
        : ONBOARDING_EVALUATION_INITIAL_AGE;
    const finalLevel = normalizeOnboardingEvaluationLevel(
        rounds[rounds.length - 1]?.nextLevel ?? rounds[rounds.length - 1]?.level,
        LEVEL_MIN
    );
    const finalEstimatedAge = getSoptEstimatedAgeForClassicLevel(finalLevel);
    const age = (averageEstimatedAge * 0.45) + (finalEstimatedAge * 0.55);

    return clamp(Math.round(age), ONBOARDING_ESTIMATED_AGE_MIN, ONBOARDING_ESTIMATED_AGE_MAX);
};

const getOnboardingResumeStorageKey = () => (
    `${getActiveUFOVStorageKey()}.${ONBOARDING_RESUME_STORAGE_SUFFIX}`
);

const normalizeOnboardingResumeRound = (rawRound, fallbackRoundIndex = 0) => {
    if (!rawRound || typeof rawRound !== 'object' || Array.isArray(rawRound)) {
        return null;
    }

    const roundIndex = Math.max(0, Math.round(Number(rawRound.roundIndex) || fallbackRoundIndex));
    const level = normalizeOnboardingEvaluationLevel(rawRound.level);
    const nextLevel = normalizeOnboardingEvaluationLevel(rawRound.nextLevel, level);

    return {
        roundIndex,
        level,
        nextLevel,
        score: clamp(Number(rawRound.score) || 0, 0, 1),
        perfect: rawRound.perfect === true,
        centerCorrect: rawRound.centerCorrect === true,
        positionCorrect: rawRound.positionCorrect === true,
    };
};

const normalizeOnboardingResumeRounds = (rawRounds, maximumRoundCount) => (
    (Array.isArray(rawRounds) ? rawRounds : [])
        .map((round, index) => normalizeOnboardingResumeRound(round, index))
        .filter((round) => round && round.roundIndex < maximumRoundCount)
        .sort((firstRound, secondRound) => firstRound.roundIndex - secondRound.roundIndex)
        .filter((round, index, rounds) => (
            index === 0 || round.roundIndex !== rounds[index - 1].roundIndex
        ))
        .slice(0, maximumRoundCount)
);

const normalizeOnboardingResumeEvaluation = (rawEvaluation = {}) => {
    const kind = rawEvaluation.kind === ONBOARDING_EVALUATION_KINDS.AGE_TEST
        ? ONBOARDING_EVALUATION_KINDS.AGE_TEST
        : ONBOARDING_EVALUATION_KINDS.CALIBRATION;
    const roundCount = getOnboardingEvaluationRoundCount(kind);
    const roundIndex = clamp(
        Math.round(Number(rawEvaluation.roundIndex) || 0),
        0,
        roundCount - 1
    );
    const normalizedRounds = normalizeOnboardingResumeRounds(rawEvaluation.rounds, roundCount);
    const currentCompletedRound = normalizedRounds.find((round) => round.roundIndex === roundIndex);
    const calibratedLevel = normalizeOnboardingEvaluationLevel(
        rawEvaluation.calibratedLevel,
        ONBOARDING_EVALUATION_INITIAL_LEVEL
    );
    const boundaryLevel = normalizeOnboardingEvaluationLevel(
        currentCompletedRound?.level ?? rawEvaluation.progress?.level,
        kind === ONBOARDING_EVALUATION_KINDS.AGE_TEST
            ? calibratedLevel
            : ONBOARDING_EVALUATION_INITIAL_LEVEL
    );
    const estimatedAgeValue = Number(rawEvaluation.estimatedAge);
    const estimatedAge = rawEvaluation.estimatedAge === null
        || rawEvaluation.estimatedAge === undefined
        || !Number.isFinite(estimatedAgeValue)
        ? null
        : clamp(
            Math.round(estimatedAgeValue),
            ONBOARDING_ESTIMATED_AGE_MIN,
            ONBOARDING_ESTIMATED_AGE_MAX
        );
    const active = rawEvaluation.active === true;

    return {
        ...createOnboardingEvaluationState(),
        active,
        kind,
        roundIndex,
        // A live or already-answered trial is deliberately not restored. Keeping
        // only earlier rounds makes refresh resume at the start of this round.
        rounds: active
            ? normalizedRounds.filter((round) => round.roundIndex < roundIndex)
            : normalizedRounds,
        calibrationRounds: normalizeOnboardingResumeRounds(
            rawEvaluation.calibrationRounds,
            ONBOARDING_CALIBRATION_ROUND_COUNT
        ),
        calibratedLevel,
        progress: createEvaluationRoundProgress(boundaryLevel),
        estimatedAge,
    };
};

const normalizeOnboardingResumeState = (rawResume = {}) => {
    if (
        !rawResume
        || typeof rawResume !== 'object'
        || Array.isArray(rawResume)
        || Number(rawResume.version) !== ONBOARDING_RESUME_VERSION
    ) {
        return null;
    }

    const parsedStep = Number(rawResume.onboardingStep);

    if (!Number.isInteger(parsedStep) || parsedStep < 0 || parsedStep > ONBOARDING_FINAL_STEP) {
        return null;
    }

    return {
        version: ONBOARDING_RESUME_VERSION,
        onboardingStep: parsedStep,
        evaluation: normalizeOnboardingResumeEvaluation(rawResume.evaluation),
    };
};

const loadOnboardingResumeState = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        const rawResume = window.localStorage.getItem(getOnboardingResumeStorageKey());

        return rawResume ? normalizeOnboardingResumeState(JSON.parse(rawResume)) : null;
    } catch (error) {
        return null;
    }
};

const saveOnboardingResumeState = (onboardingStep, evaluation) => {
    if (typeof window === 'undefined') {
        return;
    }

    const normalizedResume = normalizeOnboardingResumeState({
        version: ONBOARDING_RESUME_VERSION,
        onboardingStep,
        evaluation,
    });

    if (!normalizedResume) {
        return;
    }

    try {
        window.localStorage.setItem(
            getOnboardingResumeStorageKey(),
            JSON.stringify({
                ...normalizedResume,
                updatedAt: new Date().toISOString(),
            })
        );
    } catch (error) {
        // Storage can be unavailable in private browser contexts.
    }
};

const clearOnboardingResumeState = () => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.localStorage.removeItem(getOnboardingResumeStorageKey());
    } catch (error) {
        // Ignore storage failures; onboarding remains usable without resume.
    }
};

const normalizeUFOVProgress = (rawProgress = {}) => {
    const classicProgress = rawProgress.modes?.[MODE_IDS.CLASSIC] || rawProgress;
    const activeMode = MODE_CONFIGS[rawProgress.activeMode]?.id || MODE_IDS.CLASSIC;
    const hasTutorialSeenFlag = Object.prototype.hasOwnProperty.call(rawProgress, 'tutorialSeen');
    const tutorialSeen = hasTutorialSeenFlag
        ? rawProgress.tutorialSeen === true
        : Number(rawProgress.tutorialVersion) >= TUTORIAL_VERSION;

    return {
        ...createDefaultProgress(),
        activeMode,
        daily: normalizeDailyProgress(rawProgress.daily),
        brainGameDaily: normalizeBrainGameDailyProgress(rawProgress.brainGameDaily),
        brainGameHighScores: createBrainGameHighScores(rawProgress.brainGameHighScores),
        gameScoreHistory: createGameScoreHistory(rawProgress.gameScoreHistory),
        estimatedAge: rawProgress.estimatedAge === null || rawProgress.estimatedAge === undefined
            ? null
            : clamp(
                Math.round(Number(rawProgress.estimatedAge) || SOPT_ESTIMATED_AGE_MAX),
                SOPT_ESTIMATED_AGE_MIN,
                SOPT_ESTIMATED_AGE_MAX
            ),
        streak: normalizeStreakProgress(rawProgress.streak),
        tutorialSeen,
        tutorialVersion: TUTORIAL_VERSION,
        menuExplainerSeen: rawProgress.menuExplainerSeen === true,
        symbolVariantMode: 'assets',
        selectedAssetThemeIds: normalizeSelectedAssetThemeIds(rawProgress.selectedAssetThemeIds),
        countdownHapticsEnabled: rawProgress.countdownHapticsEnabled !== false,
        colorBlindModeEnabled: rawProgress.colorBlindModeEnabled === true,
        language: rawProgress.languageManuallySelected === true
            ? normalizeLanguage(rawProgress.language)
            : detectPreferredUFOVLanguage(),
        languageManuallySelected: rawProgress.languageManuallySelected === true,
        brainGameLevels: createBrainGameLevels(rawProgress.brainGameLevels),
        modes: {
            [MODE_IDS.CLASSIC]: normalizeModeProgress(classicProgress),
            [MODE_IDS.ADVANCED]: normalizeModeProgress(rawProgress.modes?.[MODE_IDS.ADVANCED]),
        },
    };
};

const getModeProgress = (progress, modeId) => (
    progress.modes?.[modeId] || createModeProgress()
);

const setModeProgress = (progress, modeId, modeProgress) => ({
    ...progress,
    activeMode: modeId,
    modes: {
        ...(progress.modes || {}),
        [modeId]: modeProgress,
    },
});

export const loadUFOVProgress = () => {
    if (typeof window === 'undefined') {
        return createDefaultProgress();
    }

    try {
        const storageKeys = getActiveProgressStorageKeys();
        const rawProgress = storageKeys
            .map((storageKey) => window.localStorage.getItem(storageKey))
            .find(Boolean);

        if (!rawProgress) {
            return createDefaultProgress();
        }

        const parsedProgress = JSON.parse(rawProgress);
        const normalizedProgress = normalizeUFOVProgress(parsedProgress);

        if (parsedProgress.symbolVariantMode !== normalizedProgress.symbolVariantMode) {
            window.localStorage.setItem(getActiveUFOVStorageKey(), JSON.stringify(normalizedProgress));
        }

        return normalizedProgress;
    } catch (error) {
        return createDefaultProgress();
    }
};

export const saveUFOVProgress = (progress) => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.localStorage.setItem(getActiveUFOVStorageKey(), JSON.stringify(progress));
    } catch (error) {
        // Storage can be unavailable in private browser contexts.
    }
};

export const resetUFOVProgress = () => {
    const nextProgress = createDefaultProgress();
    const activeStorageKey = getActiveUFOVStorageKey();

    saveUFOVProgress(nextProgress);

    try {
        if (activeStorageKey === UFOV_PROGRESS_STORAGE_KEY) {
            UFOV_LEGACY_PROGRESS_STORAGE_KEYS.forEach((storageKey) => window.localStorage.removeItem(storageKey));
        }
    } catch (error) {
        // Ignore storage failures when clearing legacy keys.
    }

    return nextProgress;
};

const createTrial = (level, modeId, optionsOrVariantId, configuredScenarioId = '') => {
    const trialOptions = (
        optionsOrVariantId
        && typeof optionsOrVariantId === 'object'
        && !Array.isArray(optionsOrVariantId)
    )
        ? optionsOrVariantId
        : {
            symbolVariant: optionsOrVariantId,
            scenarioId: configuredScenarioId,
        };

    return ufovTrials.createTrial(level, modeId, {
        ...trialOptions,
        symbolVariant: trialOptions.symbolVariant || getUFOVGameConfig().symbolVariant || FRUIT_SYMBOL_VARIANT_ID,
        scenarioId: trialOptions.scenarioId || '',
    });
};

const advanceProgress = (progress, answers, modeConfig) => {
    const gradedAnswers = [
        answers.centerCorrect,
        answers.positionCorrect,
    ];

    if (modeConfig.questionCount >= 3) {
        gradedAnswers.push(answers.peripheralCorrect);
    }

    const perfect = answers.centerCorrect
        && answers.positionCorrect
        && (!modeConfig.questionCount || modeConfig.questionCount < 3 || answers.peripheralCorrect);
    const correctCount = gradedAnswers.filter(Boolean).length;
    const score = correctCount / gradedAnswers.length;
    const levelDelta = perfect ? 1 : score < 0.5 ? -1 : 0;
    const nextLevel = clamp((Number(progress.level) || 0) + levelDelta, LEVEL_MIN, LEVEL_MAX);

    return {
        ...progress,
        lastLevelDelta: levelDelta,
        level: nextLevel,
        bestLevel: Math.max(progress.bestLevel || 0, nextLevel),
        trials: progress.trials + 1,
        perfectTrials: progress.perfectTrials + (perfect ? 1 : 0),
        centerHits: progress.centerHits + (answers.centerCorrect ? 1 : 0),
        positionHits: progress.positionHits + (answers.positionCorrect ? 1 : 0),
        peripheralHits: progress.peripheralHits + (answers.peripheralCorrect ? 1 : 0),
        lastPlayedAt: new Date().toISOString(),
    };
};

export default function UFOVGame({ embedded = false, onClose }) {
    const premiumName = 'Advanced';
    const gameConfig = useMemo(() => getUFOVGameConfig(), []);
    const debugConfig = useMemo(() => readUFOVDebugConfigFromUrl(), []);
    const postPurchasePreviewStep = useMemo(() => readPostPurchasePreviewStepFromUrl(), []);
    const hasDebugOverrides = useMemo(() => (
        Boolean(
            debugConfig.baseVariantId
            || debugConfig.trialVariantId
            || debugConfig.scenarioId
            || debugConfig.modeId
            || debugConfig.autostart
            || debugConfig.level !== null
        )
    ), [debugConfig]);
    const isOnboardingPreviewContext = useMemo(() => isOnboardingPreviewOrDebugContext(), []);
    const suppressOnboardingAnalytics = hasDebugOverrides
        || postPurchasePreviewStep >= 0
        || isOnboardingPreviewContext;
    const [progress, setProgress] = useState(() => loadUFOVProgress());
    const [initialOnboardingResume] = useState(() => (
        suppressOnboardingAnalytics || progress.tutorialSeen
            ? null
            : loadOnboardingResumeState()
    ));
    const configuredSymbolVariantId = useMemo(
        () => {
            if (debugConfig.baseVariantId) {
                return debugConfig.baseVariantId;
            }

            return gameConfig.symbolVariant || FRUIT_SYMBOL_VARIANT_ID;
        },
        [debugConfig.baseVariantId, gameConfig]
    );
    const configuredTrialVariantId = useMemo(
        () => debugConfig.trialVariantId || '',
        [debugConfig.trialVariantId]
    );
    const configuredScenarioId = useMemo(
        () => debugConfig.scenarioId || '',
        [debugConfig.scenarioId]
    );
    const [premiumState, setPremiumState] = useState(() => readPremiumSnapshot());
    const [activeMode, setActiveMode] = useState(() => debugConfig.modeId || progress.activeMode || MODE_IDS.CLASSIC);
    const [phase, setPhase] = useState(PHASES.MENU);
    const [trial, setTrial] = useState(null);
    const [selectedCenter, setSelectedCenter] = useState(null);
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [selectedPeripheral, setSelectedPeripheral] = useState(null);
    const [missedItDialogOpen, setMissedItDialogOpen] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [reviewFeedback, setReviewFeedback] = useState({
        open: false,
        rating: null,
        comment: '',
        submitting: false,
        error: '',
    });
    const [onboardingStep, setOnboardingStep] = useState(() => (
        hasDebugOverrides || postPurchasePreviewStep >= 0
            ? -1
            : initialOnboardingResume?.onboardingStep
                ?? (shouldForceOnboardingFromUrl() || !progress.tutorialSeen ? 0 : -1)
    ));
    const [onboardingEvaluation, setOnboardingEvaluation] = useState(() => (
        initialOnboardingResume?.evaluation
            ? {
                ...initialOnboardingResume.evaluation,
                active: false,
            }
            : createOnboardingEvaluationState()
    ));
    const [exampleCenterSelection, setExampleCenterSelection] = useState(null);
    const [examplePositionSelection, setExamplePositionSelection] = useState(null);
    const [onboardingNotice, setOnboardingNotice] = useState({ key: 0, step: -1 });
    const [trialStartedAt, setTrialStartedAt] = useState(null);
    const [paywallState, setPaywallState] = useState({ open: false, reason: 'premium', mode: MODE_IDS.CLASSIC });
    const [selectedPlan, setSelectedPlan] = useState('weekly');
    const [billingBusyAction, setBillingBusyAction] = useState('');
    const [billingStatus, setBillingStatus] = useState('');
    const [postPurchaseStep, setPostPurchaseStep] = useState(postPurchasePreviewStep);
    const [nativePackageOptions, setNativePackageOptions] = useState([]);
    const [configInitialView, setConfigInitialView] = useState('options');
    const [menuExplainerStep, setMenuExplainerStep] = useState(-1);
    const [menuExplainerReplay, setMenuExplainerReplay] = useState(false);
    const [testOnboardingNavOpen, setTestOnboardingNavOpen] = useState(false);
    const [brainGamesRoute, setBrainGamesRoute] = useState(() => (
        typeof window === 'undefined' ? null : getBrainTrainingGameFromHash(window.location.hash)
    ));
    const [selectedBrainGameId, setSelectedBrainGameId] = useState('');
    const [variantAssetsReady, setVariantAssetsReady] = useState(() => {
        const initialProgress = getModeProgress(progress, debugConfig.modeId || activeMode);
        const initialLevel = debugConfig.level ?? initialProgress.level;
        const initialScenarioContext = getUFOVScenarioRuntimeContext(
            configuredSymbolVariantId,
            initialLevel,
            {
                scenarioId: configuredScenarioId,
            }
        );
        const initialVariant = configuredTrialVariantId
            ? getUFOVSymbolVariant(configuredTrialVariantId)
            : getUFOVSymbolVariant(initialScenarioContext.activeVariantId);

        return !initialVariant.preloadAssets.length;
    });
    const [webBillingContext, setWebBillingContext] = useState({
        runtime: null,
        offering: null,
        packages: {
            weekly: null,
            lifetime: null,
        },
    });
    const billingConfig = useMemo(() => getUFOVConfig().billing || {}, []);
    const appRoutes = useMemo(() => getUFOVConfig().routes || {}, []);
    const onboardingConfig = useMemo(() => getUFOVConfig().onboarding || {}, []);
    const featureConfig = useMemo(() => getUFOVConfig().features || {}, []);
    const feedbackEnabled = isConfigFlagEnabled(featureConfig.feedbackEnabled, true);
    const nativeStoreReviewEnabled = isConfigFlagEnabled(featureConfig.storeReviewEnabled, false);
    const showOnboardingStudyPage = isConfigFlagEnabled(onboardingConfig.showStudyPage, true);
    const showOnboardingResearchPage = isConfigFlagEnabled(onboardingConfig.showResearchPage, true);
    const showOnboardingEvidenceExceptionPage = isConfigFlagEnabled(onboardingConfig.showEvidenceExceptionPage, true);
    const showOnboardingReviewPage = isConfigFlagEnabled(onboardingConfig.showReviewPage, false);
    const onboardingVisibility = [
        showOnboardingStudyPage,
        showOnboardingResearchPage,
        showOnboardingEvidenceExceptionPage,
        showOnboardingReviewPage,
    ];
    const onboardingTotalSteps = getOnboardingTotalSteps(...onboardingVisibility);
    const limits = useMemo(() => getUFOVLimits(), []);
    const isNative = useMemo(() => isNativeUFOVShell(), []);
    const paywallPurchaseRef = useRef(null);
    const nativePurchaseWatchdogRef = useRef(null);
    const nativeReviewRequestedRef = useRef(false);
    const onboardingAttributionStartedRef = useRef(false);
    const onboardingResumeBootstrappingRef = useRef(initialOnboardingResume?.evaluation?.active === true);
    const onboardingResumeStartAttemptedRef = useRef(false);
    const clearNativePurchaseWatchdog = useCallback(() => {
        if (nativePurchaseWatchdogRef.current) {
            window.clearTimeout(nativePurchaseWatchdogRef.current);
            nativePurchaseWatchdogRef.current = null;
        }
    }, []);
    const onboardingLanguage = normalizeLanguage(progress.language);
    const isIndonesianFreeAccess = isIndonesianUFOVAudience(onboardingLanguage);
    const hasTrainingAccess = premiumState.isPremium || isIndonesianFreeAccess;
    const activeProgress = useMemo(() => getModeProgress(progress, activeMode), [activeMode, progress]);
    const classicProgress = useMemo(() => getModeProgress(progress, MODE_IDS.CLASSIC), [progress]);
    const advancedProgress = useMemo(() => getModeProgress(progress, MODE_IDS.ADVANCED), [progress]);
    const hasCanonicalEstimatedAge = progress.estimatedAge !== null
        && progress.estimatedAge !== undefined
        && progress.estimatedAge !== ''
        && Number.isFinite(Number(progress.estimatedAge));
    const estimatedAge = useMemo(() => (
        hasCanonicalEstimatedAge
            ? clamp(
                Math.round(Number(progress.estimatedAge)),
                SOPT_ESTIMATED_AGE_MIN,
                SOPT_ESTIMATED_AGE_MAX
            )
            : Math.round(getSoptEstimatedAgeForClassicLevel(classicProgress.level))
    ), [classicProgress.level, hasCanonicalEstimatedAge, progress.estimatedAge]);
    const activeModeConfig = MODE_CONFIGS[activeMode] || MODE_CONFIGS[MODE_IDS.CLASSIC];
    const showOnboarding = onboardingStep >= 0 || onboardingEvaluation.active;
    const isOnboardingEvaluation = onboardingEvaluation.active;
    const isPaywallVisible = paywallState.open && !isIndonesianFreeAccess;
    const showPostPurchase = postPurchaseStep >= 0;

    useEffect(() => () => {
        clearNativePurchaseWatchdog();
    }, [clearNativePurchaseWatchdog]);

    useEffect(() => {
        if (!isNative) {
            return;
        }

        postToNativeBridge('paywall-visibility', {
            open: isPaywallVisible,
            mode: paywallState.mode,
            reason: paywallState.reason,
        });
    }, [isNative, isPaywallVisible, paywallState.mode, paywallState.reason]);

    const canShowMenuExplainer = !showOnboarding
        && !showPostPurchase
        && !isPaywallVisible
        && phase === PHASES.MENU;
    const shouldAutoShowMenuExplainer = canShowMenuExplainer
        && !hasDebugOverrides
        && !progress.menuExplainerSeen;
    const shouldShowMenuExplainer = canShowMenuExplainer
        && (menuExplainerStep >= 0 || shouldAutoShowMenuExplainer);
    const activeMenuExplainerStep = shouldShowMenuExplainer
        ? Math.max(0, menuExplainerStep)
        : -1;
    const dailyProgress = useMemo(() => normalizeDailyProgress(progress.daily), [progress.daily]);
    const brainGameDaily = useMemo(
        () => normalizeBrainGameDailyProgress(progress.brainGameDaily),
        [progress.brainGameDaily]
    );
    const dailyTarget = Math.max(1, Number(limits.dailyObjectiveRuns) || 20);
    const freeDailyLimit = Math.max(0, Number(limits.freeDailyRuns) || 0);
    const freeDailyRunsEnabled = limits.freeDailyRunsEnabled && freeDailyLimit > 0;
    const freeDailyRunsLeft = Math.max(0, freeDailyLimit - dailyProgress.runs);
    const assetThemeOptions = useMemo(() => getUFOVLiveScenarioCatalog(FRUIT_SYMBOL_VARIANT_ID), []);
    const selectedAssetThemeIds = useMemo(
        () => normalizeSelectedAssetThemeIds(progress.selectedAssetThemeIds),
        [progress.selectedAssetThemeIds]
    );
    const countdownHapticsEnabled = progress.countdownHapticsEnabled !== false;
    const colorBlindModeEnabled = progress.colorBlindModeEnabled === true;
    const languageDirection = getUFOVLanguageDirection(onboardingLanguage);
    const uiCopy = useMemo(() => getUFOVGameCopy(onboardingLanguage), [onboardingLanguage]);
    const localizedBrainGameMenuItems = useMemo(
        () => getBrainTrainingGameMenuItems(onboardingLanguage),
        [onboardingLanguage]
    );
    const paywallScreenConfig = useMemo(
        () => getPaywallScreenConfig(onboardingLanguage),
        [onboardingLanguage]
    );
    const configuredPaywallPlans = useMemo(
        () => getConfiguredPaywallPlans(onboardingLanguage, getPaywallPlanFallbacks(uiCopy)),
        [onboardingLanguage, uiCopy]
    );
    const canCloseVisiblePaywall = paywallState.reason !== 'onboarding-required'
        || paywallScreenConfig.allowCloseAfterOnboarding;

    useEffect(() => {
        if (typeof document === 'undefined') {
            return undefined;
        }

        const root = document.documentElement;
        const previousLanguage = root.lang;
        const previousDirection = root.dir;

        root.lang = onboardingLanguage;
        root.dir = languageDirection;

        return () => {
            root.lang = previousLanguage;
            root.dir = previousDirection;
        };
    }, [languageDirection, onboardingLanguage]);
    const configuredLevelOverride = debugConfig.level;
    const effectiveLevel = configuredLevelOverride ?? activeProgress.level;
    const currentScenarioContext = useMemo(() => (
        getUFOVScenarioRuntimeContext(
            configuredSymbolVariantId,
            trial?.difficulty?.level ?? effectiveLevel,
            {
                scenarioId: configuredScenarioId || trial?.requestedScenarioId || '',
            }
        )
    ), [configuredSymbolVariantId, configuredScenarioId, trial?.difficulty?.level, trial?.requestedScenarioId, effectiveLevel]);
    const symbolVariantId = useMemo(() => (
        trial?.symbolVariant
            || configuredTrialVariantId
            || getUFOVScenarioSymbolVariantId(
                configuredSymbolVariantId,
                configuredScenarioId || currentScenarioContext.requestedScenario?.id || currentScenarioContext.activeScenario?.id
            )
    ), [
        configuredSymbolVariantId,
        configuredScenarioId,
        configuredTrialVariantId,
        currentScenarioContext.requestedScenario?.id,
        currentScenarioContext.activeScenario?.id,
        trial?.symbolVariant,
    ]);
    const symbolVariant = useMemo(() => getUFOVSymbolVariant(symbolVariantId), [symbolVariantId]);
    const onboardingDemoVariantId = 'fruit-crate';
    const onboardingDemoScenarioId = 'fruit-crate';
    const onboardingDemoTrial = useMemo(() => ufovTrials.createTrial(0, MODE_IDS.CLASSIC, {
        optionLimit: 8,
        seed: `onboarding-demo-${onboardingDemoVariantId}-${onboardingDemoScenarioId}`,
        scenarioId: onboardingDemoScenarioId,
        symbolVariant: onboardingDemoVariantId,
    }), [onboardingDemoVariantId, onboardingDemoScenarioId]);
    const [onboardingDemoAssetsReady, setOnboardingDemoAssetsReady] = useState(() => (
        !getUFOVSymbolVariant(onboardingDemoVariantId).preloadAssets.length
    ));
    const [onboardingDemoBoardReady, setOnboardingDemoBoardReady] = useState(false);
    const [onboardingAdvancePending, setOnboardingAdvancePending] = useState(false);
    const onboardingDemoReady = onboardingDemoAssetsReady && onboardingDemoBoardReady;
    const onboardingCenterAnswer = onboardingDemoTrial.centerSymbol || symbolVariant.onboardingExample?.centerAnswer || EXAMPLE_CENTER_ANSWER;
    const onboardingPositionAnswer = onboardingDemoTrial.peripheralPosition?.id || EXAMPLE_POSITION_ANSWER;
    const onboardingAnswerReady = onboardingStep === ONBOARDING_CENTER_STEP
        ? exampleCenterSelection === onboardingCenterAnswer
        : onboardingStep === ONBOARDING_DIRECTION_STEP
            ? examplePositionSelection === onboardingPositionAnswer
            : true;
    const onboardingNeedsDemoAssets = onboardingStep >= ONBOARDING_INTRO_STEP && onboardingStep <= ONBOARDING_DIRECTION_STEP;
    const canAdvanceOnboarding = onboardingStep === ONBOARDING_INTRO_STEP
        ? !onboardingAdvancePending && onboardingDemoReady
        : (!onboardingNeedsDemoAssets || onboardingDemoReady) && onboardingAnswerReady;
    const visibleOnboardingNoticeKey = onboardingNotice.step === onboardingStep ? onboardingNotice.key : 0;
    const backgroundPrewarmVariantIds = useMemo(() => {
        const liveScenarioVariantIds = getUFOVLiveScenarioCatalog(FRUIT_SYMBOL_VARIANT_ID)
            .map((scenario) => getUFOVScenarioSymbolVariantId(FRUIT_SYMBOL_VARIANT_ID, scenario.id));

        return [...new Set([
            onboardingDemoVariantId,
            ...ONBOARDING_CALIBRATION_ROUNDS.map(getOnboardingEvaluationWarmupVariantId),
            ...ONBOARDING_EVALUATION_ROUNDS.map(getOnboardingEvaluationWarmupVariantId),
            ...liveScenarioVariantIds,
        ].filter(Boolean))];
    }, [onboardingDemoVariantId]);
    const currentOnboardingAnalyticsStep = useMemo(() => {
        if (isOnboardingEvaluation) {
            return getOnboardingEvaluationAnalyticsStep(
                onboardingEvaluation.roundIndex,
                onboardingEvaluation.kind
            );
        }

        if (!showOnboarding && paywallState.open && paywallState.reason === 'onboarding-required') {
            return {
                step: 'paywall_after_onboarding',
                label: 'Paywall after onboarding',
                stepIndex: ONBOARDING_ANALYTICS_TOTAL_STEPS,
            };
        }

        if (onboardingStep < 0) {
            return null;
        }

        if (onboardingStep === ONBOARDING_AGE_STEP) {
            return onboardingEvaluation.estimatedAge === null
                ? { step: 'age_test_opt_in', label: 'Age test choice', stepIndex: 17 }
                : { step: 'age_result', label: 'Estimated age result', stepIndex: 23 };
        }

        return ONBOARDING_ANALYTICS_STEP_MAP[onboardingStep] || null;
    }, [
        isOnboardingEvaluation,
        onboardingEvaluation.estimatedAge,
        onboardingEvaluation.kind,
        onboardingEvaluation.roundIndex,
        onboardingStep,
        paywallState.open,
        paywallState.reason,
        showOnboarding,
    ]);

    const ensureVariantAssetsWarm = useCallback(async (level = effectiveLevel, variantOverrideId = '') => {
        const resolvedVariantId = variantOverrideId
            || configuredTrialVariantId
            || getUFOVScenarioSymbolVariantId(
                configuredSymbolVariantId,
                configuredScenarioId
                || getUFOVScenarioRuntimeContext(
                    configuredSymbolVariantId,
                    level,
                    { scenarioId: configuredScenarioId }
                ).requestedScenario?.id
                || getUFOVScenarioRuntimeContext(configuredSymbolVariantId, level).activeScenario?.id
            );
        const warmVariant = getUFOVSymbolVariant(resolvedVariantId);

        if (!warmVariant.preloadAssets.length) {
            setVariantAssetsReady(true);
            return;
        }

        try {
            await preloadUFOVSymbolVariantAssets(warmVariant.id);
        } catch (error) {
            // Allow normal rendering to continue if the eager warmup misses.
        } finally {
            setVariantAssetsReady(true);
        }
    }, [configuredLevelOverride, configuredScenarioId, configuredSymbolVariantId, configuredTrialVariantId, effectiveLevel]);

    const preloadTrialAssets = useCallback(async (trialToWarm) => {
        if (!trialToWarm?.symbolVariant) {
            return;
        }

        const symbolIds = [
            trialToWarm.centerSymbol,
            trialToWarm.noiseSymbol,
            trialToWarm.peripheralSymbol,
            ...(trialToWarm.centerOptions || []),
            ...(trialToWarm.peripheralOptions || []),
            ...(trialToWarm.distractors || []).map((item) => item.symbol),
        ];

        await preloadUFOVSymbolAssets(trialToWarm.symbolVariant, symbolIds);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        void preloadStaticOnboardingAssets({
            includeResearchPage: showOnboardingResearchPage,
            includeStudyPage: showOnboardingStudyPage,
        }).catch(() => undefined);
    }, [showOnboardingResearchPage, showOnboardingStudyPage]);

    useEffect(() => {
        if (suppressOnboardingAnalytics || !currentOnboardingAnalyticsStep) {
            return;
        }

        void trackOnboardingSectionOnce({
            language: onboardingLanguage,
            step: currentOnboardingAnalyticsStep.step,
            step_index: currentOnboardingAnalyticsStep.stepIndex,
            step_label: currentOnboardingAnalyticsStep.label,
            step_total: ONBOARDING_ANALYTICS_TOTAL_STEPS,
        });
    }, [currentOnboardingAnalyticsStep, onboardingLanguage, suppressOnboardingAnalytics]);

    useEffect(() => {
        if (!showOnboardingEvidenceExceptionPage && onboardingStep === ONBOARDING_EXCEPTION_STEP) {
            setOnboardingStep(ONBOARDING_INTRO_STEP);
            return;
        }

        if (!showOnboardingStudyPage && onboardingStep === ONBOARDING_STUDY_STEP) {
            setOnboardingStep(showOnboardingResearchPage ? ONBOARDING_RESEARCH_STEP : ONBOARDING_PREP_STEP);
            return;
        }

        if (!showOnboardingResearchPage && onboardingStep === ONBOARDING_RESEARCH_STEP) {
            setOnboardingStep(ONBOARDING_PREP_STEP);
        }
    }, [onboardingStep, showOnboardingEvidenceExceptionPage, showOnboardingResearchPage, showOnboardingStudyPage]);

    useEffect(() => {
        if (typeof window === 'undefined' || !backgroundPrewarmVariantIds.length) {
            return undefined;
        }

        let cancelled = false;
        const prewarm = () => {
            if (cancelled) {
                return;
            }

            backgroundPrewarmVariantIds.forEach((variantId) => {
                preloadUFOVSymbolVariantAssets(variantId).catch(() => undefined);
            });
        };

        if (typeof window.requestIdleCallback === 'function') {
            const idleId = window.requestIdleCallback(prewarm, { timeout: 1200 });

            return () => {
                cancelled = true;
                window.cancelIdleCallback?.(idleId);
            };
        }

        const timeoutId = window.setTimeout(prewarm, 250);

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [backgroundPrewarmVariantIds]);

    useEffect(() => {
        let cancelled = false;

        if (!symbolVariant.preloadAssets.length) {
            setVariantAssetsReady(true);
            return undefined;
        }

        setVariantAssetsReady(false);

        preloadUFOVSymbolVariantAssets(symbolVariantId)
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) {
                    setVariantAssetsReady(true);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [symbolVariant.preloadAssets.length, symbolVariantId]);

    useEffect(() => {
        let cancelled = false;
        const demoVariant = getUFOVSymbolVariant(onboardingDemoVariantId);

        if (!demoVariant.preloadAssets.length) {
            setOnboardingDemoAssetsReady(true);
            return undefined;
        }

        setOnboardingDemoAssetsReady(false);

        preloadUFOVSymbolVariantAssets(demoVariant.id)
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) {
                    setOnboardingDemoAssetsReady(true);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [onboardingDemoVariantId]);

    useEffect(() => {
        if (!onboardingAdvancePending || onboardingStep !== ONBOARDING_INTRO_STEP || !onboardingDemoReady) {
            return;
        }

        setOnboardingAdvancePending(false);
        setOnboardingStep(ONBOARDING_EXAMPLE_STEP);
    }, [onboardingAdvancePending, onboardingDemoReady, onboardingStep]);

    const showOnboardingWrongAnswer = useCallback(() => {
        setOnboardingNotice((currentNotice) => ({
            key: currentNotice.key + 1,
            step: onboardingStep,
        }));
    }, [onboardingStep]);

    const requestNativeReviewOnce = useCallback(() => {
        if (!nativeStoreReviewEnabled || nativeReviewRequestedRef.current) {
            return;
        }

        nativeReviewRequestedRef.current = true;
        postToNativeBridge('request-review', { source: 'onboarding' });
    }, [nativeStoreReviewEnabled]);

    const openReviewFeedbackModal = useCallback(() => {
        setReviewFeedback({
            open: true,
            rating: null,
            comment: '',
            submitting: false,
            error: '',
        });
    }, []);

    const finishOnboarding = useCallback(() => {
        const shouldOpenPaywall = paywallScreenConfig.showAfterOnboarding && !hasTrainingAccess;

        if (!suppressOnboardingAnalytics) {
            void trackOnboardingSectionOnce({
                language: onboardingLanguage,
                step: 'onboarding_complete',
                step_label: 'Onboarding completed',
                step_total: ONBOARDING_ANALYTICS_TOTAL_STEPS,
            });

            if (isIndonesianFreeAccess) {
                void trackOnboardingSectionOnce({
                    language: onboardingLanguage,
                    step: 'free_access_granted',
                    step_label: 'Free access granted',
                    step_total: ONBOARDING_ANALYTICS_TOTAL_STEPS,
                });
            }
        }

        clearOnboardingResumeState();
        setReviewFeedback((currentState) => ({
            ...currentState,
            open: false,
            submitting: false,
        }));
        setOnboardingStep(-1);
        setOnboardingEvaluation(createOnboardingEvaluationState());
        setSelectedPlan(getConfiguredPaywallSelectedPlan(configuredPaywallPlans));
        setPaywallState((currentState) => ({
            ...currentState,
            open: shouldOpenPaywall,
            reason: shouldOpenPaywall ? 'onboarding-required' : currentState.reason,
            mode: activeMode,
        }));
        setBillingBusyAction('');
        setProgress((currentProgress) => {
            const nextProgress = {
                ...currentProgress,
                tutorialSeen: true,
                tutorialVersion: TUTORIAL_VERSION,
            };

            saveUFOVProgress(nextProgress);
            return nextProgress;
        });
    }, [
        activeMode,
        configuredPaywallPlans,
        isIndonesianFreeAccess,
        onboardingLanguage,
        paywallScreenConfig.showAfterOnboarding,
        hasTrainingAccess,
        suppressOnboardingAnalytics,
    ]);

    const updateReviewFeedbackRating = useCallback((rating) => {
        setReviewFeedback((currentState) => ({
            ...currentState,
            rating,
            error: '',
        }));
    }, []);

    const updateReviewFeedbackComment = useCallback((comment) => {
        setReviewFeedback((currentState) => ({
            ...currentState,
            comment,
            error: '',
        }));
    }, []);

    const skipReviewFeedback = useCallback(() => {
        finishOnboarding();
    }, [finishOnboarding]);

    const submitOnboardingReviewFeedback = useCallback(async () => {
        const rating = Number(reviewFeedback.rating);
        const comment = `${reviewFeedback.comment || ''}`.slice(0, 2000);

        if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
            setReviewFeedback((currentState) => ({
                ...currentState,
                error: 'Choose a rating before sending.',
            }));
            return;
        }

        setReviewFeedback((currentState) => ({
            ...currentState,
            rating,
            comment,
            submitting: true,
            error: '',
        }));

        try {
            await submitReviewFeedback({
                feedback_type: 'onboarding_rating',
                rating,
                comment,
                language: onboardingLanguage,
                source: 'onboarding_review',
            });
            setReviewFeedback((currentState) => ({
                ...currentState,
                open: false,
                submitting: false,
                error: '',
            }));
            if (rating >= 4) {
                requestNativeReviewOnce();
            }
            finishOnboarding();
        } catch (error) {
            setReviewFeedback((currentState) => ({
                ...currentState,
                submitting: false,
                error: error?.message || 'Feedback could not be saved.',
            }));
        }
    }, [
        finishOnboarding,
        onboardingLanguage,
        requestNativeReviewOnce,
        reviewFeedback.comment,
        reviewFeedback.rating,
    ]);

    const submitSettingsFeedback = useCallback((comment, email = '', source = 'settings') => submitReviewFeedback({
        feedback_type: 'user_feedback',
        comment: `${comment || ''}`.slice(0, 2000),
        email: `${email || ''}`.trim().slice(0, 254) || undefined,
        language: onboardingLanguage,
        source,
    }), [onboardingLanguage]);

    const startOnboardingEvaluationRound = useCallback(async (
        roundIndex,
        level = ONBOARDING_EVALUATION_INITIAL_LEVEL,
        kind = ONBOARDING_EVALUATION_KINDS.CALIBRATION
    ) => {
        const safeLevel = normalizeOnboardingEvaluationLevel(level);
        const roundConfig = getOnboardingEvaluationRoundConfig(roundIndex, kind);
        const evaluationVariantId = getOnboardingEvaluationWarmupVariantId(roundConfig);
        const baseTrial = createTrial(safeLevel, MODE_IDS.CLASSIC, {
            scenarioId: roundConfig.scenarioId,
            symbolVariant: roundConfig.symbolVariant,
        });
        const nextTrial = kind === ONBOARDING_EVALUATION_KINDS.CALIBRATION
            ? {
                ...baseTrial,
                difficulty: {
                    ...baseTrial.difficulty,
                    exposureMs: clamp(
                        baseTrial.difficulty.exposureMs + ONBOARDING_CALIBRATION_EXPOSURE_BONUS_MS,
                        80,
                        1280
                    ),
                },
            }
            : baseTrial;

        await ensureVariantAssetsWarm(safeLevel, evaluationVariantId);
        await preloadTrialAssets(nextTrial);
        setTrial(nextTrial);
        setSelectedCenter(null);
        setSelectedPosition(null);
        setSelectedPeripheral(null);
        setFeedback(null);
        setTrialStartedAt(Date.now());
        setPhase(PHASES.PRERENDER);
        setOnboardingEvaluation((currentState) => ({
            ...currentState,
            active: true,
            kind,
            roundIndex,
            progress: createEvaluationRoundProgress(safeLevel),
        }));
    }, [ensureVariantAssetsWarm, preloadTrialAssets]);

    const startOnboardingEvaluation = useCallback(async (
        kind = ONBOARDING_EVALUATION_KINDS.CALIBRATION,
        initialLevel = ONBOARDING_EVALUATION_INITIAL_LEVEL
    ) => {
        const safeInitialLevel = normalizeOnboardingEvaluationLevel(initialLevel);
        const roundConfig = getOnboardingEvaluationRoundConfig(0, kind);
        const initialEvaluationVariantId = getOnboardingEvaluationWarmupVariantId(roundConfig);
        const baseTrial = createTrial(safeInitialLevel, MODE_IDS.CLASSIC, {
            scenarioId: roundConfig.scenarioId,
            symbolVariant: roundConfig.symbolVariant,
        });
        const nextTrial = kind === ONBOARDING_EVALUATION_KINDS.CALIBRATION
            ? {
                ...baseTrial,
                difficulty: {
                    ...baseTrial.difficulty,
                    exposureMs: clamp(
                        baseTrial.difficulty.exposureMs + ONBOARDING_CALIBRATION_EXPOSURE_BONUS_MS,
                        80,
                        1280
                    ),
                },
            }
            : baseTrial;

        await ensureVariantAssetsWarm(safeInitialLevel, initialEvaluationVariantId);
        await preloadTrialAssets(nextTrial);
        setOnboardingEvaluation((currentState) => ({
            ...createOnboardingEvaluationState(),
            active: true,
            kind,
            calibrationRounds: currentState.calibrationRounds || [],
            calibratedLevel: kind === ONBOARDING_EVALUATION_KINDS.AGE_TEST
                ? safeInitialLevel
                : ONBOARDING_EVALUATION_INITIAL_LEVEL,
            progress: createEvaluationRoundProgress(safeInitialLevel),
        }));
        setTrial(nextTrial);
        setSelectedCenter(null);
        setSelectedPosition(null);
        setSelectedPeripheral(null);
        setFeedback(null);
        setTrialStartedAt(Date.now());
        setPhase(PHASES.PRERENDER);
    }, [ensureVariantAssetsWarm, preloadTrialAssets]);

    useEffect(() => {
        const restoredEvaluation = initialOnboardingResume?.evaluation;

        if (
            !restoredEvaluation?.active
            || onboardingResumeStartAttemptedRef.current
        ) {
            return;
        }

        onboardingResumeStartAttemptedRef.current = true;

        void startOnboardingEvaluationRound(
            restoredEvaluation.roundIndex,
            restoredEvaluation.progress?.level,
            restoredEvaluation.kind
        ).catch(() => {
            setOnboardingEvaluation((currentState) => ({
                ...currentState,
                active: false,
            }));
            setOnboardingStep(ONBOARDING_PREP_STEP);
            setTrial(null);
            setPhase(PHASES.MENU);
        }).finally(() => {
            onboardingResumeBootstrappingRef.current = false;
        });
    }, [initialOnboardingResume, startOnboardingEvaluationRound]);

    useEffect(() => {
        if (suppressOnboardingAnalytics || onboardingResumeBootstrappingRef.current) {
            return;
        }

        if (progress.tutorialSeen || onboardingStep < 0) {
            clearOnboardingResumeState();
            return;
        }

        if (onboardingStep === ONBOARDING_SETTINGS_STEP && !onboardingEvaluation.active) {
            clearOnboardingResumeState();
            return;
        }

        saveOnboardingResumeState(onboardingStep, onboardingEvaluation);
    }, [
        onboardingEvaluation,
        onboardingStep,
        progress.tutorialSeen,
        suppressOnboardingAnalytics,
    ]);

    const handleOnboardingDemoPrewarmReady = useCallback(() => {
        setOnboardingDemoBoardReady(true);
    }, []);

    const advanceOnboarding = useCallback(() => {
        if (
            onboardingStep === ONBOARDING_SETTINGS_STEP
            && !onboardingAttributionStartedRef.current
            && !suppressOnboardingAnalytics
        ) {
            onboardingAttributionStartedRef.current = true;
            postToNativeBridge('attribution-event', {
                eventName: 'onboarding_started',
            });
        }

        if (onboardingStep === ONBOARDING_INTRO_STEP && !onboardingDemoReady) {
            setOnboardingAdvancePending(true);
            return;
        }

        if (onboardingStep === ONBOARDING_CENTER_STEP && exampleCenterSelection !== onboardingCenterAnswer) {
            showOnboardingWrongAnswer();
            return;
        }

        if (onboardingStep === ONBOARDING_DIRECTION_STEP && examplePositionSelection !== onboardingPositionAnswer) {
            showOnboardingWrongAnswer();
            return;
        }

        if (onboardingStep === ONBOARDING_BRAIN_GAMES_EVIDENCE_STEP && !showOnboardingEvidenceExceptionPage) {
            setOnboardingStep(ONBOARDING_INTRO_STEP);
            return;
        }

        if (onboardingStep === ONBOARDING_STUDY_STEP && !showOnboardingResearchPage) {
            setOnboardingStep(ONBOARDING_PREP_STEP);
            return;
        }

        if (onboardingStep === ONBOARDING_RESEARCH_STEP) {
            setOnboardingStep(ONBOARDING_PREP_STEP);
            return;
        }

        if (onboardingStep === ONBOARDING_PREP_STEP) {
            startOnboardingEvaluation();
            return;
        }

        if (onboardingStep === ONBOARDING_AGE_STEP) {
            setOnboardingStep(ONBOARDING_SHARE_STEP);
            return;
        }

        if (onboardingStep === ONBOARDING_SHARE_STEP) {
            setOnboardingStep(ONBOARDING_DEMOGRAPHICS_STEP);
            return;
        }

        if (onboardingStep === ONBOARDING_DEMOGRAPHICS_STEP) {
            setOnboardingStep(ONBOARDING_IMPROVEMENT_STEP);
            return;
        }

        if (onboardingStep === ONBOARDING_IMPROVEMENT_STEP) {
            setOnboardingStep(ONBOARDING_CONTACT_STEP);
            return;
        }

        if (onboardingStep === ONBOARDING_CONTACT_STEP) {
            setOnboardingStep(ONBOARDING_LIFETIME_STEP);
            return;
        }

        if (onboardingStep === ONBOARDING_LIFETIME_STEP) {
            if (showOnboardingReviewPage) {
                setOnboardingStep(ONBOARDING_REVIEW_STEP);
            } else {
                finishOnboarding();
            }
            return;
        }

        if (onboardingStep === ONBOARDING_REVIEW_STEP) {
            openReviewFeedbackModal();
            return;
        }

        if (onboardingStep >= ONBOARDING_FINAL_STEP) {
            finishOnboarding();
            return;
        }

        setOnboardingStep((currentStep) => currentStep + 1);
    }, [
        exampleCenterSelection,
        examplePositionSelection,
        finishOnboarding,
        onboardingCenterAnswer,
        onboardingDemoReady,
        onboardingPositionAnswer,
        onboardingStep,
        openReviewFeedbackModal,
        showOnboardingResearchPage,
        showOnboardingEvidenceExceptionPage,
        showOnboardingReviewPage,
        showOnboardingWrongAnswer,
        startOnboardingEvaluation,
        suppressOnboardingAnalytics,
    ]);

    const jumpToTestOnboardingPage = useCallback((target) => {
        if (typeof window !== 'undefined' && window.location.hash.startsWith(BRAIN_GAMES_HASH)) {
            window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
        }

        setBrainGamesRoute(null);
        setTestOnboardingNavOpen(false);
        setReviewFeedback({
            open: false,
            rating: null,
            comment: '',
            submitting: false,
            error: '',
        });
        setOnboardingAdvancePending(false);
        setPaywallState((currentState) => ({ ...currentState, open: false }));
        setOnboardingEvaluation(createOnboardingEvaluationState());
        setTrial(null);
        setFeedback(null);
        setPhase(PHASES.MENU);

        if (target === 'evaluation') {
            setOnboardingStep(ONBOARDING_PREP_STEP);
            void startOnboardingEvaluation();
            return;
        }

        if (target === 'paywall') {
            setOnboardingStep(-1);
            setSelectedPlan(getConfiguredPaywallSelectedPlan(configuredPaywallPlans));
            setPaywallState({ open: true, reason: 'onboarding-required', mode: activeMode });
            return;
        }

        if (target === 'menu') {
            setOnboardingStep(-1);
            return;
        }

        setOnboardingStep(Number(target));
    }, [activeMode, configuredPaywallPlans, startOnboardingEvaluation]);

    const handleExampleCenterSelect = useCallback((symbolId) => {
        if (symbolId !== onboardingCenterAnswer) {
            setExampleCenterSelection(null);
            showOnboardingWrongAnswer();
            return;
        }

        setExampleCenterSelection(symbolId);
    }, [onboardingCenterAnswer, showOnboardingWrongAnswer]);

    const handleExamplePositionSelect = useCallback((positionId) => {
        if (positionId !== onboardingPositionAnswer) {
            setExamplePositionSelection(null);
            showOnboardingWrongAnswer();
            return;
        }

        setExamplePositionSelection(positionId);
    }, [onboardingPositionAnswer, showOnboardingWrongAnswer]);

    const returnToMenu = useCallback(() => {
        setPaywallState((currentState) => ({ ...currentState, open: false }));
        setTrial(null);
        setSelectedCenter(null);
        setSelectedPosition(null);
        setSelectedPeripheral(null);
        setFeedback(null);
        setTrialStartedAt(null);
        setPhase(PHASES.MENU);
        setOnboardingEvaluation(createOnboardingEvaluationState());
    }, []);

    const openBrainGame = useCallback((gameId = '') => {
        const normalizedGameId = `${gameId || ''}`.trim();

        if (!isBrainTrainingGameMenuId(normalizedGameId)) {
            return;
        }

        const nextHash = `${BRAIN_GAMES_HASH}/${normalizedGameId}`;

        setMenuExplainerStep(-1);
        setBrainGamesRoute(normalizedGameId);

        if (typeof window !== 'undefined' && window.location.hash !== nextHash) {
            window.location.hash = nextHash;
        }
    }, []);

    const closeBrainGames = useCallback(() => {
        if (typeof window !== 'undefined' && window.location.hash.startsWith(BRAIN_GAMES_HASH)) {
            window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
        }

        setBrainGamesRoute(null);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const syncBrainGamesRoute = () => {
            const nextBrainGamesRoute = getBrainTrainingGameFromHash(window.location.hash);
            setBrainGamesRoute(nextBrainGamesRoute);

            if (nextBrainGamesRoute === null && window.location.hash.startsWith(BRAIN_GAMES_HASH)) {
                window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
            }
        };

        window.addEventListener('hashchange', syncBrainGamesRoute);
        syncBrainGamesRoute();

        return () => window.removeEventListener('hashchange', syncBrainGamesRoute);
    }, []);

    const closePaywall = useCallback(() => {
        clearNativePurchaseWatchdog();
        paywallPurchaseRef.current = null;
        setPaywallState((currentState) => ({ ...currentState, open: false }));
        setBillingBusyAction('');
    }, [clearNativePurchaseWatchdog]);

    useEffect(() => {
        if (!isIndonesianFreeAccess || !paywallState.open) {
            return;
        }

        clearNativePurchaseWatchdog();
        paywallPurchaseRef.current = null;
        setBillingBusyAction('');
        setBillingStatus('');
        setPaywallState((currentState) => ({ ...currentState, open: false }));
    }, [clearNativePurchaseWatchdog, isIndonesianFreeAccess, paywallState.open]);

    const openPostPurchaseFlow = useCallback(() => {
        clearNativePurchaseWatchdog();
        paywallPurchaseRef.current = null;
        setBillingBusyAction('');
        setBillingStatus('');
        setPaywallState((currentState) => ({ ...currentState, open: false }));
        setPhase(PHASES.MENU);
        setPostPurchaseStep(0);
    }, [clearNativePurchaseWatchdog]);

    const advancePostPurchase = useCallback(() => {
        setPostPurchaseStep((currentStep) => {
            if (currentStep < 0) {
                return currentStep;
            }

            const nextStep = currentStep + 1;
            return nextStep >= POST_PURCHASE_TOTAL_STEPS ? -1 : nextStep;
        });
    }, []);

    const openPaywall = useCallback(({ reason = 'premium', mode = activeMode } = {}) => {
        if (isIndonesianFreeAccess) {
            return;
        }

        clearNativePurchaseWatchdog();
        setSelectedPlan(getConfiguredPaywallSelectedPlan(configuredPaywallPlans));
        setBillingStatus('');
        setPaywallState({
            open: true,
            reason,
            mode,
        });
    }, [activeMode, clearNativePurchaseWatchdog, configuredPaywallPlans, isIndonesianFreeAccess]);

    useEffect(() => {
        if (shouldAutoShowMenuExplainer && menuExplainerStep < 0) {
            setMenuExplainerReplay(false);
            setMenuExplainerStep(0);
        }
    }, [menuExplainerStep, shouldAutoShowMenuExplainer]);

    const markMenuExplainerSeen = useCallback(() => {
        setProgress((currentProgress) => {
            if (currentProgress.menuExplainerSeen) {
                return currentProgress;
            }

            const nextProgress = {
                ...currentProgress,
                menuExplainerSeen: true,
            };

            saveUFOVProgress(nextProgress);
            return nextProgress;
        });
    }, []);

    const dismissMenuExplainer = useCallback(() => {
        setMenuExplainerStep(-1);
        setMenuExplainerReplay(false);
        markMenuExplainerSeen();
    }, [markMenuExplainerSeen]);

    const completeMenuExplainer = useCallback(() => {
        const shouldOfferAccess = !menuExplainerReplay
            && !hasTrainingAccess
            && (!freeDailyRunsEnabled || freeDailyRunsLeft <= 0);

        dismissMenuExplainer();

        if (shouldOfferAccess) {
            openPaywall({ reason: 'menu-tour', mode: activeMode });
        }
    }, [
        activeMode,
        dismissMenuExplainer,
        freeDailyRunsEnabled,
        freeDailyRunsLeft,
        hasTrainingAccess,
        menuExplainerReplay,
        openPaywall,
    ]);

    const openMenuExplainer = useCallback(() => {
        setMenuExplainerReplay(true);
        setMenuExplainerStep(0);
    }, []);

    const advanceMenuExplainer = useCallback(() => {
        const nextStep = activeMenuExplainerStep + 1;

        if (nextStep >= MENU_EXPLAINER_STEPS.length) {
            completeMenuExplainer();
            return;
        }

        setMenuExplainerStep(nextStep);
    }, [activeMenuExplainerStep, completeMenuExplainer]);

    useEffect(() => {
        let isMounted = true;

        const syncPremium = async () => {
            try {
                const appUserId = await ensureAnonymousAppUserId();
                if (!isMounted) {
                    return;
                }

                setPremiumState((currentState) => ({
                    ...currentState,
                    appUserId,
                }));

                if (!isNative) {
                    const snapshot = await refreshWebPremiumState({ force: true, appUserId });
                    if (isMounted) {
                        setPremiumState(snapshot);
                    }
                }
            } catch (error) {
                if (isMounted) {
                    setPremiumState((currentState) => writePremiumSnapshot({
                        ...currentState,
                        error: error?.message || 'Premium sync failed.',
                    }));
                }
            }
        };

        syncPremium();

        return () => {
            isMounted = false;
        };
    }, [isNative]);

    useEffect(() => {
        const handleBridgeEvent = (event) => {
            const payload = parseBridgePayload(event);

            if (!payload?.type) {
                return;
            }

            if (
                payload.type === 'premium-status'
                || payload.type === 'purchase-success'
                || payload.type === 'restore-purchases-completed'
            ) {
                clearNativePurchaseWatchdog();
                paywallPurchaseRef.current = null;
                setBillingBusyAction('');
                if (Array.isArray(payload.availablePackages)) {
                    setNativePackageOptions(normalizeNativePackageOptions(payload));
                }
                const snapshot = syncPremiumSnapshotFromNative(payload);
                setPremiumState(snapshot);
                if (payload.type === 'purchase-success' && snapshot.isPremium) {
                    openPostPurchaseFlow();
                }
                return;
            }

            if (payload.type === 'purchase-error' || payload.type === 'restore-purchases-error') {
                clearNativePurchaseWatchdog();
                paywallPurchaseRef.current = null;
                setBillingBusyAction('');
                setBillingStatus(getBillingErrorMessage(payload.reason, uiCopy.checkoutUnavailable));
                setPremiumState((currentState) => writePremiumSnapshot({
                    ...currentState,
                    error: payload.debugReason || payload.reason || uiCopy.premiumFlowInterrupted,
                    source: 'native',
                }));
            }
        };

        window.addEventListener('message', handleBridgeEvent);
        window.addEventListener('ufov-native-message', handleBridgeEvent);

        return () => {
            window.removeEventListener('message', handleBridgeEvent);
            window.removeEventListener('ufov-native-message', handleBridgeEvent);
        };
    }, [clearNativePurchaseWatchdog, openPostPurchaseFlow, uiCopy]);

    useEffect(() => {
        if (!isPaywallVisible) {
            return undefined;
        }

        if (isNative) {
            setBillingStatus(
                premiumState.isPremium
                    ? formatCopyTemplate(uiCopy.activeOnDevice, { name: premiumName })
                    : uiCopy.secureCheckoutDevice
            );
            return undefined;
        }

        let cancelled = false;

        const loadPricing = async () => {
            try {
                const context = await loadWebBillingContext({
                    force: true,
                    appUserId: premiumState.appUserId || getStoredAppUserId(),
                });

                if (cancelled) {
                    return;
                }

                setWebBillingContext({
                    runtime: context.runtime,
                    offering: context.offering,
                    packages: context.packages,
                });
                setBillingStatus(
                    context.snapshot.isPremium
                        ? formatCopyTemplate(uiCopy.activeInBrowser, { name: premiumName })
                        : uiCopy.revenueCatCheckout
                );
            } catch (error) {
                if (!cancelled) {
                    setBillingStatus(getBillingErrorMessage(error, uiCopy.pricingLoading));
                }
            }
        };

        void loadPricing();

        return () => {
            cancelled = true;
        };
    }, [isNative, isPaywallVisible, premiumState.appUserId, premiumState.isPremium, premiumName, uiCopy]);

    useEffect(() => {
        if (!premiumState.isPremium || !isPaywallVisible) {
            return;
        }

        setBillingBusyAction('');
        setBillingStatus(formatCopyTemplate(isNative ? uiCopy.activeOnDevice : uiCopy.activeInBrowser, { name: premiumName }));

        if (paywallState.mode === MODE_IDS.ADVANCED) {
            setActiveMode(MODE_IDS.ADVANCED);
            setProgress((currentProgress) => {
                const nextProgress = {
                    ...currentProgress,
                    activeMode: MODE_IDS.ADVANCED,
                };

                saveUFOVProgress(nextProgress);
                return nextProgress;
            });
        }

        setPaywallState((currentState) => ({ ...currentState, open: false }));
    }, [isNative, isPaywallVisible, paywallState.mode, premiumState.isPremium, premiumName, uiCopy]);

    const handleModeSelect = useCallback((modeId) => {
        setSelectedBrainGameId('');
        setActiveMode(modeId);
        setProgress((currentProgress) => {
            const nextProgress = {
                ...currentProgress,
                activeMode: modeId,
            };

            saveUFOVProgress(nextProgress);
            return nextProgress;
        });
    }, []);

    const handleBrainGameSelect = useCallback((gameId) => {
        const normalizedGameId = `${gameId || ''}`.trim();

        if (!isBrainTrainingGameMenuId(normalizedGameId)) {
            return;
        }

        setMenuExplainerStep(-1);
        setSelectedBrainGameId((currentGameId) => (
            currentGameId === normalizedGameId ? '' : normalizedGameId
        ));
    }, []);

    const handleLevelChange = useCallback((itemId, level) => {
        const parsedLevel = Number(level);

        if (!Number.isFinite(parsedLevel)) {
            return;
        }

        const normalizedItemId = `${itemId || ''}`.trim();
        const soptMode = normalizedItemId === 'sopt-classic'
            ? MODE_IDS.CLASSIC
            : normalizedItemId === 'sopt-advanced'
                ? MODE_IDS.ADVANCED
                : null;

        if (!soptMode && !isBrainTrainingGameMenuId(normalizedItemId)) {
            return;
        }

        setProgress((currentProgress) => {
            let nextProgress;

            if (soptMode) {
                const nextLevel = clamp(Math.round(parsedLevel), LEVEL_MIN, LEVEL_MAX);
                const currentModeProgress = getModeProgress(currentProgress, soptMode);
                const nextModeProgress = {
                    ...currentModeProgress,
                    level: nextLevel,
                    bestLevel: Math.max(currentModeProgress.bestLevel || 0, nextLevel),
                    lastLevelDelta: nextLevel - (Number(currentModeProgress.level) || 0),
                };

                if (nextLevel === currentModeProgress.level) {
                    return currentProgress;
                }

                nextProgress = {
                    ...currentProgress,
                    modes: {
                        ...(currentProgress.modes || {}),
                        [soptMode]: nextModeProgress,
                    },
                };
            } else {
                const nextLevel = normalizeBrainGameLevel(normalizedItemId, parsedLevel);
                const currentLevels = createBrainGameLevels(currentProgress.brainGameLevels);

                if (currentLevels[normalizedItemId] === nextLevel) {
                    return currentProgress;
                }

                nextProgress = {
                    ...currentProgress,
                    brainGameLevels: {
                        ...currentLevels,
                        [normalizedItemId]: nextLevel,
                    },
                };
            }

            saveUFOVProgress(nextProgress);
            return nextProgress;
        });
    }, []);

    const handleBrainGameRunComplete = useCallback((runSummary = {}) => {
        const gameId = `${runSummary.gameId || ''}`.trim();

        if (!isBrainTrainingGameMenuId(gameId)) {
            return;
        }

        const elapsedMs = Number(
            runSummary.elapsedMs
            ?? runSummary.metrics?.durationMs
            ?? runSummary.metrics?.elapsedMs
            ?? 0
        ) || 0;

        setProgress((currentProgress) => {
            const nextBrainGameDaily = advanceBrainGameDailyProgress(
                currentProgress.brainGameDaily,
                gameId,
                elapsedMs
            );
            const nextBrainGameHighScores = setBrainGameHighScore(
                currentProgress.brainGameHighScores,
                gameId,
                runSummary.highScore
            );
            const nextGameScoreHistory = recordGameScore(
                currentProgress.gameScoreHistory,
                gameId,
                {
                    ...runSummary.performance,
                    durationMs: elapsedMs,
                    level: runSummary.metrics?.level,
                }
            );
            const nextProgress = {
                ...currentProgress,
                brainGameDaily: nextBrainGameDaily,
                brainGameHighScores: nextBrainGameHighScores,
                gameScoreHistory: nextGameScoreHistory,
            };

            saveUFOVProgress(nextProgress);
            return nextProgress;
        });
    }, []);

    const handleSelectedAssetThemesChange = useCallback((themeIds) => {
        const nextSelectedThemeIds = normalizeSelectedAssetThemeIds(themeIds);

        setProgress((currentProgress) => {
            const currentThemeIds = normalizeSelectedAssetThemeIds(currentProgress.selectedAssetThemeIds);
            const unchanged = currentThemeIds.length === nextSelectedThemeIds.length
                && currentThemeIds.every((themeId, index) => themeId === nextSelectedThemeIds[index]);

            if (unchanged) {
                return currentProgress;
            }

            const nextProgress = {
                ...currentProgress,
                selectedAssetThemeIds: nextSelectedThemeIds,
            };

            saveUFOVProgress(nextProgress);
            return nextProgress;
        });
    }, []);

    const handleCountdownHapticsChange = useCallback((enabled) => {
        setProgress((currentProgress) => {
            const nextEnabled = enabled !== false;

            if ((currentProgress.countdownHapticsEnabled !== false) === nextEnabled) {
                return currentProgress;
            }

            const nextProgress = {
                ...currentProgress,
                countdownHapticsEnabled: nextEnabled,
            };

            saveUFOVProgress(nextProgress);
            return nextProgress;
        });
    }, []);

    const handleColorBlindModeChange = useCallback((enabled) => {
        setProgress((currentProgress) => {
            const nextEnabled = enabled === true;

            if ((currentProgress.colorBlindModeEnabled === true) === nextEnabled) {
                return currentProgress;
            }

            const nextProgress = {
                ...currentProgress,
                colorBlindModeEnabled: nextEnabled,
            };

            saveUFOVProgress(nextProgress);
            return nextProgress;
        });
    }, []);

    const handleOnboardingLanguageChange = useCallback((language) => {
        const nextLanguage = normalizeLanguage(language);

        setProgress((currentProgress) => {
            if (
                normalizeLanguage(currentProgress.language) === nextLanguage
                && currentProgress.languageManuallySelected === true
            ) {
                return currentProgress;
            }

            const nextProgress = {
                ...currentProgress,
                language: nextLanguage,
                languageManuallySelected: true,
            };

            saveUFOVProgress(nextProgress);
            return nextProgress;
        });
    }, []);

    const handleReviewerAccessSubmit = useCallback(async (code) => {
        const result = await validateReviewerAccessCode(code);

        if (!result.valid) {
            return { valid: false };
        }

        grantReviewerAccess(result.accessDurationSeconds);
        const snapshot = writePremiumSnapshot({
            isPremium: true,
            entitlements: getBillingEntitlementIds(),
            appUserId: premiumState.appUserId || getStoredAppUserId(),
            managementUrl: '',
            source: 'reviewer-access',
            error: '',
        });

        setPremiumState(snapshot);
        setPaywallState((currentState) => ({ ...currentState, open: false }));
        setBillingStatus('');

        return { valid: true };
    }, [premiumState.appUserId]);

    const handleRestorePurchases = useCallback(async () => {
        if (isNative) {
            setBillingBusyAction('restore');
            setBillingStatus(uiCopy.checkingStoreAccount);
            postToNativeBridge('restore-purchases');
            return;
        }

        try {
            setBillingBusyAction('restore');
            const snapshot = await refreshWebPremiumState({
                force: true,
                appUserId: premiumState.appUserId || getStoredAppUserId(),
            });

            setPremiumState(snapshot);
            setBillingStatus(
                snapshot.isPremium
                    ? formatCopyTemplate(uiCopy.activeInBrowser, { name: premiumName })
                    : uiCopy.noPremiumBrowser
            );
        } catch (error) {
            setBillingStatus(getBillingErrorMessage(error, uiCopy.restoreFailed));
        } finally {
            setBillingBusyAction('');
        }
    }, [isNative, premiumState.appUserId, premiumName, uiCopy]);

    const handlePlanPurchase = useCallback(async (planId) => {
        if (hasTrainingAccess || paywallPurchaseRef.current) {
            return;
        }

        const configuredPlan = configuredPaywallPlans.find((plan) => plan.id === planId)
            || getPaywallPlanCopy(planId, uiCopy);
        const nativePackage = isNative ? resolveNativePackageForPlan(nativePackageOptions, configuredPlan) : null;
        const planKind = configuredPlan.planKind || (nativePackage ? getNativePackagePlanKind(nativePackage) : planId);
        const rcPackage = webBillingContext.packages[planKind] || webBillingContext.packages[planId];
        const purchaseTarget = isNative && configuredPlan.nativePackageIdentifiers?.length
            ? configuredPlan
            : nativePackage || rcPackage || configuredPlan;

        if (planKind === 'weekly') {
            void trackTikTokEvent('initiate_free_trial', {
                app_user_id: premiumState.appUserId || getStoredAppUserId(),
                offering_id: billingConfig.offeringId || '',
                plan: planId,
                plan_kind: planKind,
                mode: paywallState.mode,
                reason: paywallState.reason,
            });
        }

        if (isNative) {
            clearNativePurchaseWatchdog();
            paywallPurchaseRef.current = planId;
            setBillingBusyAction(planId);
            const planName = configuredPlan.label || (nativePackage ? getNativePackageLabel(nativePackage, uiCopy) : uiCopy.premium);
            setBillingStatus(formatCopyTemplate(uiCopy.openingPlanCheckout, { name: planName }));
            nativePurchaseWatchdogRef.current = window.setTimeout(() => {
                if (paywallPurchaseRef.current === planId) {
                    setBillingStatus(uiCopy.waitingStoreConfirmation);
                    nativePurchaseWatchdogRef.current = window.setTimeout(() => {
                        if (paywallPurchaseRef.current === planId) {
                            setBillingStatus(uiCopy.waitingStoreConfirmationLong);
                        }
                        nativePurchaseWatchdogRef.current = null;
                    }, NATIVE_PURCHASE_LONG_WAITING_MS - NATIVE_PURCHASE_WAITING_MS);
                    return;
                }
                nativePurchaseWatchdogRef.current = null;
            }, NATIVE_PURCHASE_WAITING_MS);
            postToNativeBridge(
                `purchase+${planKind}`,
                buildNativePurchasePayload(planKind, purchaseTarget, {
                    offeringId: billingConfig.offeringId || '',
                    mode: paywallState.mode,
                    reason: paywallState.reason,
                })
            );
            return;
        }

        const purchaseLink = await buildWebPurchaseLink({
            reason: paywallState.reason,
            mode: paywallState.mode,
            packageId: configuredPlan.packageIdentifier || configuredPlan.packageId || '',
            offeringId: billingConfig.offeringId || '',
        });

        const runtime = webBillingContext.runtime;

        try {
            paywallPurchaseRef.current = planId;
            setBillingBusyAction(planId);

            if (runtime?.purchases && rcPackage) {
                const result = await runtime.purchases.purchasePackage(rcPackage);
                const activeEntitlements = Object.keys(result?.customerInfo?.entitlements?.active || {});
                const entitlementIds = getBillingEntitlementIds();
                const snapshot = writePremiumSnapshot({
                    isPremium: activeEntitlements.some((identifier) => entitlementIds.includes(identifier)),
                    entitlements: activeEntitlements,
                    managementUrl: result?.customerInfo?.managementURL || '',
                    appUserId: premiumState.appUserId || getStoredAppUserId(),
                    source: 'web-sdk',
                    error: '',
                });

                setPremiumState(snapshot);
                setBillingStatus(formatCopyTemplate(uiCopy.activeInBrowser, { name: premiumName }));
                if (snapshot.isPremium) {
                    openPostPurchaseFlow();
                }
                return;
            }

            if (purchaseLink) {
                window.location.assign(purchaseLink);
                return;
            }

            setBillingStatus(uiCopy.planUnavailable);
        } catch (error) {
            setBillingStatus(
                isCancelledError(error)
                    ? uiCopy.purchaseCancelled
                    : getBillingErrorMessage(error, uiCopy.checkoutUnavailable)
            );
        } finally {
            paywallPurchaseRef.current = null;
            setBillingBusyAction('');
        }
    }, [billingConfig.offeringId, clearNativePurchaseWatchdog, configuredPaywallPlans, hasTrainingAccess, isNative, nativePackageOptions, openPostPurchaseFlow, paywallState.mode, paywallState.reason, premiumName, premiumState.appUserId, uiCopy, webBillingContext.packages, webBillingContext.runtime]);

    useEffect(() => {
        if (!billingStatus || premiumState.isPremium || billingBusyAction) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setBillingStatus('');
        }, isBillingConfigurationMessage(billingStatus) ? 15000 : 3600);

        return () => window.clearTimeout(timeoutId);
    }, [billingBusyAction, billingStatus, premiumState.isPremium]);

    const beginTrial = useCallback(async (levelOverride = null) => {
        const canUseFreeDailyRun = freeDailyRunsEnabled && freeDailyRunsLeft > 0;

        if (!hasTrainingAccess && !canUseFreeDailyRun) {
            openPaywall({
                reason: freeDailyRunsEnabled ? 'free-runs-used' : 'play-required',
                mode: activeMode,
            });
            return;
        }

        const shouldUseSelectedAssetTheme = !configuredScenarioId
            && !configuredTrialVariantId
            && configuredSymbolVariantId === FRUIT_SYMBOL_VARIANT_ID
            && selectedAssetThemeIds.length > 0;
        const selectedScenarioId = shouldUseSelectedAssetTheme
            ? randomItem(selectedAssetThemeIds)
            : configuredScenarioId;
        const parsedLevelOverride = Number(levelOverride);
        const hasLevelOverride = levelOverride !== null
            && levelOverride !== undefined
            && levelOverride !== ''
            && Number.isFinite(parsedLevelOverride);
        const nextLevel = hasLevelOverride ? parsedLevelOverride : effectiveLevel;
        const nextTrial = createTrial(
            nextLevel,
            activeMode,
            {
                scenarioId: selectedScenarioId,
                symbolVariant: configuredTrialVariantId || configuredSymbolVariantId,
            }
        );
        await ensureVariantAssetsWarm(nextLevel, nextTrial.symbolVariant);
        await preloadTrialAssets(nextTrial);
        setTrial(nextTrial);
        setSelectedCenter(null);
        setSelectedPosition(null);
        setSelectedPeripheral(null);
        setFeedback(null);
        setMissedItDialogOpen(false);
        setTrialStartedAt(Date.now());
        setPhase(PHASES.PRERENDER);
    }, [
        activeMode,
        configuredScenarioId,
        configuredSymbolVariantId,
        configuredTrialVariantId,
        effectiveLevel,
        ensureVariantAssetsWarm,
        freeDailyRunsEnabled,
        freeDailyRunsLeft,
        openPaywall,
        preloadTrialAssets,
        hasTrainingAccess,
        selectedAssetThemeIds,
    ]);

    const startSelectedMenuItem = useCallback(() => {
        if (selectedBrainGameId) {
            openBrainGame(selectedBrainGameId);
            return;
        }

        void beginTrial();
    }, [beginTrial, openBrainGame, selectedBrainGameId]);

    useEffect(() => {
        if (!trial) {
            return undefined;
        }

        let timeoutId = null;

        if (phase === PHASES.FIXATION) {
            timeoutId = window.setTimeout(() => setPhase(PHASES.STIMULUS), trial.difficulty.fixationMs);
        }

        if (phase === PHASES.STIMULUS) {
            timeoutId = window.setTimeout(() => setPhase(PHASES.MASK), trial.difficulty.exposureMs);
        }

        if (phase === PHASES.MASK) {
            timeoutId = window.setTimeout(() => setPhase(PHASES.ANSWER_CENTER), trial.difficulty.maskMs);
        }

        return () => {
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [phase, trial]);

    useEffect(() => {
        if (!isNative || !countdownHapticsEnabled || phase !== PHASES.FIXATION || !trial) {
            return undefined;
        }

        const timeoutIds = COUNTDOWN_VIBRATION_DIGITS.map((digit, index) => (
            window.setTimeout(() => {
                postToNativeBridge('timer-vibrate', {
                    digit,
                    durationMs: COUNTDOWN_VIBRATION_MS,
                    source: 'countdown',
                });
            }, index * 1000)
        ));

        return () => {
            timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
        };
    }, [countdownHapticsEnabled, isNative, phase, trial]);

    const handleBoardPrewarmReady = useCallback(() => {
        waitForQuestionPrewarmLayers().then(() => {
            setPhase((currentPhase) => (
                currentPhase === PHASES.PRERENDER ? PHASES.FIXATION : currentPhase
            ));
        });
    }, []);

    const debugAutostartAttemptedRef = useRef(false);

    useEffect(() => {
        if (
            !debugConfig.autostart
            || debugAutostartAttemptedRef.current
            || showOnboarding
            || phase !== PHASES.MENU
            || trial
        ) {
            return;
        }

        debugAutostartAttemptedRef.current = true;
        beginTrial();
    }, [beginTrial, debugConfig.autostart, phase, showOnboarding, trial]);

    const submitAnswer = useCallback((answerOverrides = {}) => {
        const missed = answerOverrides.missed === true;
        const retryImmediately = answerOverrides.retryImmediately === true;
        const answerCenter = missed ? null : answerOverrides.center || selectedCenter;
        const answerPosition = missed ? null : answerOverrides.position || selectedPosition;
        const answerPeripheral = missed ? null : answerOverrides.peripheral || selectedPeripheral;

        if (!trial || (!missed && (!answerCenter || !answerPosition))) {
            return;
        }

        if (!missed && !isOnboardingEvaluation && activeModeConfig.questionCount >= 3 && !answerPeripheral) {
            return;
        }

        if (isOnboardingEvaluation) {
            const currentEvaluationProgress = onboardingEvaluation.progress || createEvaluationRoundProgress(
                ONBOARDING_EVALUATION_INITIAL_LEVEL
            );
            const previousLevel = currentEvaluationProgress.level;
            const centerCorrect = !missed && answerCenter === trial.centerSymbol;
            const positionCorrect = !missed && answerPosition === trial.peripheralPosition.id;
            const answers = {
                centerCorrect,
                positionCorrect,
                peripheralCorrect: true,
            };
            const score = getOnboardingRoundScore(centerCorrect, positionCorrect);
            const nextEvaluationProgress = advanceOnboardingEvaluationProgress(currentEvaluationProgress, answers);
            const perfect = centerCorrect && positionCorrect;
            const anyCorrect = centerCorrect || positionCorrect;
            const difficultyChanged = nextEvaluationProgress.level !== previousLevel;

            setOnboardingEvaluation((currentState) => ({
                ...currentState,
                progress: nextEvaluationProgress,
                rounds: [
                    ...currentState.rounds,
                    {
                        roundIndex: currentState.roundIndex,
                        level: previousLevel,
                        nextLevel: nextEvaluationProgress.level,
                        score,
                        perfect,
                        centerCorrect,
                        positionCorrect,
                    },
                ],
            }));
            setFeedback({
                centerCorrect,
                positionCorrect,
                peripheralCorrect: true,
                perfect,
                levelDelta: nextEvaluationProgress.lastLevelDelta,
                difficultyChanged,
                previousLevel,
                modeId: MODE_IDS.CLASSIC,
                label: perfect ? 'Clean read' : anyCorrect ? 'Split read' : 'Signal lost',
                title: perfect
                    ? difficultyChanged
                        ? 'New speed unlocked.'
                        : 'Both targets captured.'
                    : anyCorrect
                        ? 'One target held.'
                        : 'Level adjusted.',
            });
            setTrialStartedAt(null);
            setPhase(PHASES.RESULT);
            return;
        }

        const modeProgress = getModeProgress(progress, activeMode);
        const previousLevel = modeProgress.level;
        const centerCorrect = !missed && answerCenter === trial.centerSymbol;
        const positionCorrect = !missed && answerPosition === trial.peripheralPosition.id;
        const peripheralCorrect = activeModeConfig.questionCount >= 3
            ? !missed && answerPeripheral === trial.peripheralSymbol
            : true;
        const answers = {
            centerCorrect,
            positionCorrect,
            peripheralCorrect,
        };
        const nextModeProgress = advanceProgress(modeProgress, answers, activeModeConfig);
        const progressWithMode = setModeProgress(progress, activeMode, nextModeProgress);
        const correctAnswerCount = [
            centerCorrect,
            positionCorrect,
            ...(activeModeConfig.questionCount >= 3 ? [peripheralCorrect] : []),
        ].filter(Boolean).length;
        const nextGameScoreHistory = recordGameScore(
            progressWithMode.gameScoreHistory,
            `sopt-${activeMode}`,
            {
                metric: 'score',
                value: correctAnswerCount,
                maxValue: activeModeConfig.questionCount,
                level: nextModeProgress.level,
                durationMs: trialStartedAt ? Math.max(1, Date.now() - trialStartedAt) : null,
            },
            nextModeProgress.lastPlayedAt || new Date()
        );
        const nextProgress = {
            ...progressWithMode,
            daily: advanceDailyProgress(progressWithMode.daily, trialStartedAt, activeMode),
            gameScoreHistory: nextGameScoreHistory,
            streak: advanceStreakProgress(progressWithMode.streak),
        };
        const perfect = centerCorrect && positionCorrect && peripheralCorrect;
        const anyCorrect = centerCorrect || positionCorrect || (activeModeConfig.questionCount >= 3 && peripheralCorrect);
        const difficultyChanged = nextModeProgress.level !== previousLevel;

        setProgress(nextProgress);
        saveUFOVProgress(nextProgress);
        setFeedback({
            centerCorrect,
            positionCorrect,
            peripheralCorrect,
            perfect,
            levelDelta: nextModeProgress.lastLevelDelta,
            difficultyChanged,
            previousLevel,
            modeId: activeMode,
            label: perfect ? 'Clean read' : anyCorrect ? 'Split read' : 'Signal lost',
            title: perfect
                ? difficultyChanged
                    ? 'New speed unlocked.'
                    : activeModeConfig.questionCount >= 3 ? 'All targets captured.' : 'Both targets captured.'
                : anyCorrect
                    ? 'One target held.'
                    : 'Level adjusted.',
        });
        setTrialStartedAt(null);
        if (retryImmediately) {
            void beginTrial(nextModeProgress.level);
        } else {
            setPhase(PHASES.RESULT);
        }
    }, [
        activeMode,
        activeModeConfig,
        beginTrial,
        isOnboardingEvaluation,
        onboardingEvaluation.progress,
        onboardingEvaluation.roundIndex,
        progress,
        selectedCenter,
        selectedPeripheral,
        selectedPosition,
        trial,
        trialStartedAt,
    ]);

    const handleMissedItRetry = useCallback(() => {
        setMissedItDialogOpen(false);
        submitAnswer({ missed: true, retryImmediately: true });
    }, [submitAnswer]);

    const handleOnboardingEvaluationNext = useCallback(() => {
        const evaluationKind = onboardingEvaluation.kind || ONBOARDING_EVALUATION_KINDS.CALIBRATION;
        const evaluationRoundCount = getOnboardingEvaluationRoundCount(evaluationKind);
        const nextRoundIndex = onboardingEvaluation.roundIndex + 1;

        if (nextRoundIndex < evaluationRoundCount) {
            startOnboardingEvaluationRound(
                nextRoundIndex,
                onboardingEvaluation.progress?.level,
                evaluationKind
            );
            return;
        }

        const evaluatedLevel = normalizeOnboardingEvaluationLevel(onboardingEvaluation.progress?.level);

        if (evaluationKind === ONBOARDING_EVALUATION_KINDS.CALIBRATION) {
            const classicLevel = getClassicLevelFromOnboardingEvaluationLevel(evaluatedLevel);

            setOnboardingEvaluation((currentState) => ({
                ...currentState,
                active: false,
                calibrationRounds: currentState.rounds,
                calibratedLevel: evaluatedLevel,
                rounds: [],
                estimatedAge: null,
            }));
            setProgress((currentProgress) => {
                const currentClassicProgress = getModeProgress(currentProgress, MODE_IDS.CLASSIC);
                const nextClassicProgress = {
                    ...currentClassicProgress,
                    level: classicLevel,
                    bestLevel: Math.max(currentClassicProgress.bestLevel || 0, classicLevel),
                    lastLevelDelta: classicLevel - (currentClassicProgress.level || 0),
                };
                const nextProgress = {
                    ...setModeProgress(currentProgress, MODE_IDS.CLASSIC, nextClassicProgress),
                    estimatedAge: null,
                };

                saveUFOVProgress(nextProgress);
                return nextProgress;
            });
        } else {
            const canonicalEstimatedAge = getOnboardingEstimatedAge(onboardingEvaluation.rounds);
            const ageMatchedClassicLevel = getSoptClassicLevelForEstimatedAge(canonicalEstimatedAge);

            setOnboardingEvaluation((currentState) => ({
                ...currentState,
                active: false,
                estimatedAge: canonicalEstimatedAge,
            }));
            setProgress((currentProgress) => {
                const currentClassicProgress = getModeProgress(currentProgress, MODE_IDS.CLASSIC);
                const nextProgress = {
                    ...currentProgress,
                    estimatedAge: canonicalEstimatedAge,
                    modes: {
                        ...(currentProgress.modes || {}),
                        [MODE_IDS.CLASSIC]: {
                            ...currentClassicProgress,
                            level: ageMatchedClassicLevel,
                            bestLevel: Math.max(currentClassicProgress.bestLevel || 0, ageMatchedClassicLevel),
                            lastLevelDelta: ageMatchedClassicLevel - (currentClassicProgress.level || 0),
                        },
                    },
                };

                saveUFOVProgress(nextProgress);
                return nextProgress;
            });
        }

        setTrial(null);
        setSelectedCenter(null);
        setSelectedPosition(null);
        setSelectedPeripheral(null);
        setFeedback(null);
        setTrialStartedAt(null);
        setPhase(PHASES.MENU);
        setOnboardingStep(ONBOARDING_AGE_STEP);
    }, [
        onboardingEvaluation.kind,
        onboardingEvaluation.progress?.level,
        onboardingEvaluation.roundIndex,
        onboardingEvaluation.rounds,
        startOnboardingEvaluationRound,
    ]);

    const startOnboardingAgeTest = useCallback(() => {
        void startOnboardingEvaluation(
            ONBOARDING_EVALUATION_KINDS.AGE_TEST,
            onboardingEvaluation.calibratedLevel
        );
    }, [onboardingEvaluation.calibratedLevel, startOnboardingEvaluation]);

    const skipOnboardingAgeTest = useCallback(() => {
        setOnboardingEvaluation((currentState) => ({
            ...currentState,
            active: false,
            rounds: [],
            estimatedAge: null,
        }));
        setOnboardingStep(ONBOARDING_DEMOGRAPHICS_STEP);
    }, []);

    const handleOnboardingDemographicsSubmit = useCallback((answers = {}) => {
        const parsedAge = Number(answers.age);
        const selfReportedAge = Number.isInteger(parsedAge) && parsedAge >= 18 && parsedAge <= 120
            ? parsedAge
            : null;
        const requestedGenderCode = `${answers.gender || ''}`.trim();
        const selfReportedGenderCode = ONBOARDING_DEMOGRAPHIC_GENDER_CODES.has(requestedGenderCode)
            ? requestedGenderCode
            : null;
        const analyticsStep = ONBOARDING_ANALYTICS_STEP_MAP[ONBOARDING_DEMOGRAPHICS_STEP];

        if (!suppressOnboardingAnalytics) {
            void updateOnboardingSession({
                language: onboardingLanguage,
                self_reported_age_years: selfReportedAge,
                self_reported_gender_code: selfReportedGenderCode,
                step: analyticsStep.step,
                step_index: analyticsStep.stepIndex,
                step_label: analyticsStep.label,
                step_total: ONBOARDING_ANALYTICS_TOTAL_STEPS,
            });
        }

        setOnboardingStep(ONBOARDING_IMPROVEMENT_STEP);
    }, [onboardingLanguage, suppressOnboardingAnalytics]);

    const handleReset = useCallback(() => {
        const nextProgress = resetUFOVProgress();
        const nextPremiumState = clearPremiumSnapshot();

        clearNativePurchaseWatchdog();
        clearAnonymousRankingIdentity();
        clearOnboardingResumeState();
        paywallPurchaseRef.current = null;
        setProgress(nextProgress);
        setPremiumState(nextPremiumState);
        setPaywallState({ open: false, reason: 'premium', mode: MODE_IDS.CLASSIC });
        setBillingStatus('');
        setBillingBusyAction('');
        setPostPurchaseStep(-1);
        setFeedback(null);
        setTrial(null);
        setSelectedCenter(null);
        setSelectedPosition(null);
        setSelectedPeripheral(null);
        setActiveMode(MODE_IDS.CLASSIC);
        setTrialStartedAt(null);
        setOnboardingStep(0);
        setOnboardingEvaluation(createOnboardingEvaluationState());
        setExampleCenterSelection(null);
        setExamplePositionSelection(null);
        setMenuExplainerStep(-1);
        setPhase(PHASES.MENU);
    }, [clearNativePurchaseWatchdog]);

    const handleKeyDown = useCallback((event) => {
        const targetTag = event.target?.tagName?.toLowerCase();

        if (['a', 'button', 'input', 'select', 'textarea'].includes(targetTag)) {
            return;
        }

        if (showOnboarding && !isOnboardingEvaluation) {
            if (
                onboardingStep === ONBOARDING_TASTE_STEP
                || onboardingStep === ONBOARDING_BELIEF_STEP
                || (onboardingStep === ONBOARDING_AGE_STEP && onboardingEvaluation.estimatedAge === null)
                || onboardingStep === ONBOARDING_DEMOGRAPHICS_STEP
            ) {
                return;
            }

            if (INPUT_KEYS.cancel.includes(event.key)) {
                event.preventDefault();
                finishOnboarding();
                return;
            }

            if (INPUT_KEYS.confirm.includes(event.key)) {
                event.preventDefault();
                if (onboardingStep >= ONBOARDING_FINAL_STEP) {
                    finishOnboarding();
                    return;
                }

                advanceOnboarding();
            }

            return;
        }

        if (isOnboardingEvaluation && INPUT_KEYS.cancel.includes(event.key)) {
            event.preventDefault();
            return;
        }

        if (isOnboardingEvaluation && INPUT_KEYS.confirm.includes(event.key)) {
            event.preventDefault();
            return;
        }

        if (paywallState.open) {
            if (INPUT_KEYS.cancel.includes(event.key)) {
                event.preventDefault();
                if (canCloseVisiblePaywall) {
                    closePaywall();
                }
                return;
            }

            if (INPUT_KEYS.confirm.includes(event.key) && !hasTrainingAccess) {
                event.preventDefault();
                void handlePlanPurchase(selectedPlan);
            }

            return;
        }

        if (showPostPurchase) {
            if (INPUT_KEYS.confirm.includes(event.key)) {
                event.preventDefault();
                advancePostPurchase();
            }

            return;
        }

        if (activeMenuExplainerStep >= 0 && phase === PHASES.MENU) {
            if (INPUT_KEYS.cancel.includes(event.key)) {
                event.preventDefault();
                dismissMenuExplainer();
            } else if (INPUT_KEYS.confirm.includes(event.key)) {
                event.preventDefault();
                advanceMenuExplainer();
            }

            return;
        }

        if (INPUT_KEYS.cancel.includes(event.key)) {
            event.preventDefault();

            if (phase === PHASES.MENU) {
                onClose?.();
            } else {
                returnToMenu();
            }
        }

        if (INPUT_KEYS.confirm.includes(event.key)) {
            event.preventDefault();
            const questionCount = isOnboardingEvaluation
                ? MODE_CONFIGS[MODE_IDS.CLASSIC].questionCount
                : activeModeConfig.questionCount;

            if (phase === PHASES.MENU) {
                void beginTrial();
            } else if (phase === PHASES.PROGRESS) {
                returnToMenu();
            } else if (phase === PHASES.RESULT) {
                void beginTrial();
            } else if (phase === PHASES.ANSWER_CENTER && selectedCenter) {
                setPhase(questionCount >= 3 ? PHASES.ANSWER_PERIPHERAL : PHASES.ANSWER_POSITION);
            } else if (phase === PHASES.ANSWER_POSITION && selectedPosition) {
                submitAnswer();
            } else if (phase === PHASES.ANSWER_PERIPHERAL && selectedPeripheral) {
                setPhase(PHASES.ANSWER_POSITION);
            }
        }
    }, [activeMenuExplainerStep, activeModeConfig.questionCount, advanceMenuExplainer, advanceOnboarding, advancePostPurchase, beginTrial, canCloseVisiblePaywall, closePaywall, dismissMenuExplainer, finishOnboarding, handlePlanPurchase, hasTrainingAccess, isOnboardingEvaluation, onClose, onboardingEvaluation.estimatedAge, onboardingStep, paywallState.open, phase, returnToMenu, selectedCenter, selectedPeripheral, selectedPosition, selectedPlan, showOnboarding, showPostPurchase, submitAnswer]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);

        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const dailyProgressByItem = useMemo(() => {
        const modeDaily = dailyProgress.modes || {};
        const itemProgress = {
            'sopt-classic': { runs: Math.max(0, Number(modeDaily[MODE_IDS.CLASSIC]?.runs) || 0) },
            'sopt-advanced': { runs: Math.max(0, Number(modeDaily[MODE_IDS.ADVANCED]?.runs) || 0) },
        };

        localizedBrainGameMenuItems.forEach((game) => {
            itemProgress[game.id] = {
                runs: Math.max(0, Number(brainGameDaily.games?.[game.id]?.runs) || 0),
            };
        });

        return itemProgress;
    }, [brainGameDaily.games, dailyProgress.modes, localizedBrainGameMenuItems]);
    const currentQuestionCount = isOnboardingEvaluation ? MODE_CONFIGS[MODE_IDS.CLASSIC].questionCount : activeModeConfig.questionCount;
    const centerStep = currentQuestionCount >= 3 ? '1 / 3' : '1 / 2';
    const positionStep = currentQuestionCount >= 3 ? '3 / 3' : '2 / 2';
    const peripheralStep = '2 / 3';
    const handleCenterAnswerSelect = useCallback((symbolId) => {
        setSelectedCenter(symbolId);
        setPhase(currentQuestionCount >= 3 ? PHASES.ANSWER_PERIPHERAL : PHASES.ANSWER_POSITION);
    }, [currentQuestionCount]);
    const handlePeripheralAnswerSelect = useCallback((symbolId) => {
        setSelectedPeripheral(symbolId);
        setPhase(PHASES.ANSWER_POSITION);
    }, []);
    const handlePositionAnswerSelect = useCallback((positionId) => {
        setSelectedPosition(positionId);
        submitAnswer({ position: positionId });
    }, [submitAnswer]);
    const isCenterQuestionPhase = phase === PHASES.ANSWER_CENTER;
    const isPositionQuestionPhase = phase === PHASES.ANSWER_POSITION;
    const isPeripheralQuestionPhase = phase === PHASES.ANSWER_PERIPHERAL;
    const shouldPrewarmQuestionScreens = trial && (isLiveTrialPhase(phase) || isCenterQuestionPhase || isPositionQuestionPhase || isPeripheralQuestionPhase);
    const shouldMountOnboardingCenterQuestion = showOnboarding && isOnboardingEvaluation && !paywallState.open && shouldPrewarmQuestionScreens;
    const shouldMountOnboardingPositionQuestion = showOnboarding && isOnboardingEvaluation && !paywallState.open && shouldPrewarmQuestionScreens;
    const shouldMountCenterQuestion = !showOnboarding && !showPostPurchase && !paywallState.open && shouldPrewarmQuestionScreens;
    const shouldMountPositionQuestion = !showOnboarding && !showPostPurchase && !paywallState.open && shouldPrewarmQuestionScreens;
    const shouldMountPeripheralQuestion = !showOnboarding && !showPostPurchase && !paywallState.open && shouldPrewarmQuestionScreens && activeModeConfig.questionCount >= 3;
    const planViews = useMemo(() => configuredPaywallPlans.map((plan) => {
        const nativePackage = isNative ? resolveNativePackageForPlan(nativePackageOptions, plan) : null;
        const rcPackage = webBillingContext.packages[plan.planKind] || webBillingContext.packages[plan.id];

        return createConfiguredPlanView({
            plan,
            copy: uiCopy,
            isNative,
            nativePackage,
            rcPackage,
            billingConfig,
        });
    }), [billingConfig, configuredPaywallPlans, isNative, nativePackageOptions, uiCopy, webBillingContext.packages]);

    useEffect(() => {
        if (paywallState.open && planViews.length && !planViews.some((plan) => plan.id === selectedPlan)) {
            setSelectedPlan(planViews[0].id);
        }
    }, [paywallState.open, planViews, selectedPlan]);

    return (
        <UFOVSymbolVariantProvider variantId={symbolVariantId}>
            <div
                aria-label={uiCopy.appAriaLabel}
                aria-modal={embedded ? undefined : 'true'}
                dir={languageDirection}
                lang={onboardingLanguage}
                className={[
                    'UFOV-game',
                    embedded ? 'is-embedded' : '',
                    colorBlindModeEnabled ? 'is-color-blind-mode' : '',
                    symbolVariant.rootClassName || '',
                ].filter(Boolean).join(' ')}
                role={embedded ? undefined : 'dialog'}
            >
                <style>{UFOVGameStyles}{BrainTrainingGamesStyles}{OnboardingBrainGameTasteStyles}{TEST_ONBOARDING_STYLES}</style>
                {isTestOnboardingRoute() && (
                    <div className={`UFOV-test-nav ${testOnboardingNavOpen ? 'is-open' : ''}`}>
                        <button
                            type="button"
                            className="UFOV-test-nav__toggle"
                            onClick={() => setTestOnboardingNavOpen((open) => !open)}
                        >
                            DEV
                        </button>
                        <div className="UFOV-test-nav__panel">
                            <div className="UFOV-test-nav__title">Jump to onboarding page</div>
                            {TEST_ONBOARDING_PAGES.map(([key, label, target]) => {
                                if (
                                    (target === ONBOARDING_EXCEPTION_STEP && !showOnboardingEvidenceExceptionPage)
                                    || (target === ONBOARDING_REVIEW_STEP && !showOnboardingReviewPage)
                                ) {
                                    return null;
                                }

                                const active = target === 'evaluation'
                                    ? isOnboardingEvaluation
                                    : target === 'paywall'
                                        ? paywallState.open
                                        : target === 'menu'
                                            ? !showOnboarding && !paywallState.open
                                            : !isOnboardingEvaluation && onboardingStep === target;

                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        className={`UFOV-test-nav__page ${active ? 'is-active' : ''}`}
                                        onClick={() => jumpToTestOnboardingPage(target)}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                            <div className="UFOV-test-nav__title">Jump to brain game</div>
                            {BRAIN_TRAINING_GAME_MENU_ITEMS.map((game) => (
                                <button
                                    key={game.id}
                                    type="button"
                                    className={`UFOV-test-nav__page ${brainGamesRoute === game.id ? 'is-active' : ''}`}
                                    onClick={() => {
                                        openBrainGame(game.id);
                                        setTestOnboardingNavOpen(false);
                                    }}
                                >
                                    {game.title}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <div className="UFOV-game__device">
                {brainGamesRoute !== null ? (
                    <BrainTrainingGames
                        brainGameHighScores={progress.brainGameHighScores}
                        colorBlindModeEnabled={colorBlindModeEnabled}
                        initialGameId={brainGamesRoute || ''}
                        initialLevel={normalizeBrainGameLevel(brainGamesRoute, progress.brainGameLevels?.[brainGamesRoute])}
                        language={onboardingLanguage}
                        onClose={closeBrainGames}
                        onRunComplete={handleBrainGameRunComplete}
                    />
                ) : (
                    <>
                {showOnboarding && !isOnboardingEvaluation && onboardingStep === ONBOARDING_SETTINGS_STEP && (
                    <UFOVOnboardingSettingsScreen
                        countdownHapticsEnabled={countdownHapticsEnabled}
                        language={onboardingLanguage}
                        onCountdownHapticsChange={handleCountdownHapticsChange}
                        onLanguageChange={handleOnboardingLanguageChange}
                        onNext={advanceOnboarding}
                        onReviewerAccessSubmit={handleReviewerAccessSubmit}
                        totalSteps={onboardingTotalSteps}
                    />
                )}

                {showOnboarding && !isOnboardingEvaluation && onboardingStep === ONBOARDING_TASTE_STEP && (
                    <OnboardingBrainGameTaste
                        colorBlindModeEnabled={colorBlindModeEnabled}
                        language={onboardingLanguage}
                        onComplete={() => setOnboardingStep(ONBOARDING_GAMES_COUNT_STEP)}
                        progressLabel={getOnboardingProgressLabel(ONBOARDING_TASTE_STEP, ...onboardingVisibility)}
                    />
                )}

                {showOnboarding && !isOnboardingEvaluation && onboardingStep === ONBOARDING_GAMES_COUNT_STEP && (
                    <OnboardingTasteStatementScreen
                        language={onboardingLanguage}
                        onNext={advanceOnboarding}
                        progressLabel={getOnboardingProgressLabel(ONBOARDING_GAMES_COUNT_STEP, ...onboardingVisibility)}
                        variant="catalog"
                    />
                )}

                {showOnboarding && !isOnboardingEvaluation && onboardingStep === ONBOARDING_BELIEF_STEP && (
                    <OnboardingTasteStatementScreen
                        language={onboardingLanguage}
                        onAnswer={() => setOnboardingStep(ONBOARDING_BRAIN_GAMES_EVIDENCE_STEP)}
                        progressLabel={getOnboardingProgressLabel(ONBOARDING_BELIEF_STEP, ...onboardingVisibility)}
                        variant="belief"
                    />
                )}

                {showOnboarding && !isOnboardingEvaluation && onboardingStep === ONBOARDING_BRAIN_GAMES_EVIDENCE_STEP && (
                    <OnboardingTasteStatementScreen
                        language={onboardingLanguage}
                        onNext={advanceOnboarding}
                        progressLabel={getOnboardingProgressLabel(ONBOARDING_BRAIN_GAMES_EVIDENCE_STEP, ...onboardingVisibility)}
                        variant="evidence"
                    />
                )}

                {showOnboarding && !isOnboardingEvaluation && showOnboardingEvidenceExceptionPage && onboardingStep === ONBOARDING_EXCEPTION_STEP && (
                    <OnboardingTasteStatementScreen
                        language={onboardingLanguage}
                        onNext={advanceOnboarding}
                        progressLabel={getOnboardingProgressLabel(ONBOARDING_EXCEPTION_STEP, ...onboardingVisibility)}
                        variant="exception"
                    />
                )}

                {showOnboarding && !isOnboardingEvaluation && onboardingStep >= ONBOARDING_INTRO_STEP && onboardingStep <= ONBOARDING_DIRECTION_STEP && (
                    <UFOVOnboardingScreen
                        buttonBusy={onboardingAdvancePending && onboardingStep === ONBOARDING_INTRO_STEP}
                        canContinue={canAdvanceOnboarding}
                        demoReady={onboardingDemoReady}
                        demoVariantId={onboardingDemoVariantId}
                        demoTrial={onboardingDemoTrial}
                        language={onboardingLanguage}
                        onDemoPrewarmReady={handleOnboardingDemoPrewarmReady}
                        onNext={advanceOnboarding}
                        onSelectCenter={handleExampleCenterSelect}
                        onSelectPosition={handleExamplePositionSelect}
                        positions={POSITIONS}
                        progressStepIndex={getOnboardingVisibleStepIndex(onboardingStep, ...onboardingVisibility)}
                        selectedCenter={exampleCenterSelection}
                        selectedPosition={examplePositionSelection}
                        stepIndex={onboardingStep - ONBOARDING_INTRO_STEP}
                        totalSteps={onboardingTotalSteps}
                        wrongNoticeKey={visibleOnboardingNoticeKey}
                    />
                )}

                {showOnboarding && !isOnboardingEvaluation && onboardingStep === ONBOARDING_PREP_STEP && (
                    <UFOVOnboardingPanelScreen
                        language={onboardingLanguage}
                        onNext={advanceOnboarding}
                        progressLabel={getOnboardingProgressLabel(ONBOARDING_PREP_STEP, ...onboardingVisibility)}
                    />
                )}

                {showOnboarding && isOnboardingEvaluation && !paywallState.open && isLiveTrialPhase(phase) && trial && (
                    <UFOVPlayScreen language={onboardingLanguage} phase={phase} trial={trial} onPrewarmReady={handleBoardPrewarmReady} />
                )}

                {shouldMountOnboardingCenterQuestion && (
                    <div
                        aria-hidden={isCenterQuestionPhase ? undefined : 'true'}
                        className={`UFOV-game__persistent-screen-layer ${isCenterQuestionPhase ? 'is-visible' : 'is-prewarming'}`.trim()}
                        data-ufov-question-prewarm="true"
                        inert={isCenterQuestionPhase ? undefined : ''}
                    >
                        <UFOVCenterQuestionScreen
                            language={onboardingLanguage}
                            onContinue={() => setPhase(PHASES.ANSWER_POSITION)}
                            onSelectCenter={handleCenterAnswerSelect}
                            selectedCenter={selectedCenter}
                            showFooter={false}
                            step={centerStep}
                            trial={trial}
                        />
                    </div>
                )}

                {shouldMountOnboardingPositionQuestion && (
                    <div
                        aria-hidden={isPositionQuestionPhase ? undefined : 'true'}
                        className={`UFOV-game__persistent-screen-layer ${isPositionQuestionPhase ? 'is-visible' : 'is-prewarming'}`.trim()}
                        data-ufov-question-prewarm="true"
                        inert={isPositionQuestionPhase ? undefined : ''}
                    >
                        <UFOVPositionQuestionScreen
                            buttonLabel={uiCopy.scoreRun}
                            language={onboardingLanguage}
                            onContinue={submitAnswer}
                            onSelectPosition={handlePositionAnswerSelect}
                            positions={POSITIONS}
                            selectedPosition={selectedPosition}
                            showFooter={false}
                            step={positionStep}
                            trial={trial}
                        />
                    </div>
                )}

                {showOnboarding && isOnboardingEvaluation && !paywallState.open && phase === PHASES.RESULT && (
                    <UFOVResultScreen
                        feedback={feedback}
                        language={onboardingLanguage}
                        nextLabel={onboardingEvaluation.roundIndex >= getOnboardingEvaluationRoundCount(onboardingEvaluation.kind) - 1
                            ? onboardingEvaluation.kind === ONBOARDING_EVALUATION_KINDS.CALIBRATION
                                ? uiCopy.continueLabel
                                : uiCopy.seeResult
                            : uiCopy.nextRound}
                        onNext={handleOnboardingEvaluationNext}
                        progress={onboardingEvaluation.progress}
                        trial={trial}
                    />
                )}

                {showOnboarding && !isOnboardingEvaluation && onboardingStep === ONBOARDING_AGE_STEP && onboardingEvaluation.estimatedAge === null && (
                    <UFOVOnboardingAgeOptInScreen
                        language={onboardingLanguage}
                        onCalculate={startOnboardingAgeTest}
                        onSkip={skipOnboardingAgeTest}
                        progressLabel={getOnboardingProgressLabel(ONBOARDING_AGE_STEP, ...onboardingVisibility)}
                    />
                )}

                {showOnboarding && !isOnboardingEvaluation && onboardingStep === ONBOARDING_AGE_STEP && onboardingEvaluation.estimatedAge !== null && (
                    <UFOVOnboardingAgeScreen
                        estimatedAge={onboardingEvaluation.estimatedAge || getOnboardingEstimatedAge(onboardingEvaluation.rounds)}
                        language={onboardingLanguage}
                        onNext={advanceOnboarding}
                        progressLabel={getOnboardingProgressLabel(ONBOARDING_AGE_STEP, ...onboardingVisibility)}
                    />
                )}

                {showOnboarding && !isOnboardingEvaluation && onboardingStep === ONBOARDING_SHARE_STEP && (
                    <UFOVOnboardingShareScreen
                        estimatedAge={onboardingEvaluation.estimatedAge || getOnboardingEstimatedAge(onboardingEvaluation.rounds)}
                        language={onboardingLanguage}
                        onContinue={advanceOnboarding}
                        progressLabel={getOnboardingProgressLabel(ONBOARDING_SHARE_STEP, ...onboardingVisibility)}
                    />
                )}

                {showOnboarding && !isOnboardingEvaluation && onboardingStep === ONBOARDING_DEMOGRAPHICS_STEP && (
                    <UFOVOnboardingDemographicsScreen
                        language={onboardingLanguage}
                        onSkip={() => handleOnboardingDemographicsSubmit()}
                        onSubmit={handleOnboardingDemographicsSubmit}
                        progressLabel={getOnboardingProgressLabel(ONBOARDING_DEMOGRAPHICS_STEP, ...onboardingVisibility)}
                    />
                )}

                {showOnboarding && !isOnboardingEvaluation && onboardingStep === ONBOARDING_IMPROVEMENT_STEP && (
                    <OnboardingTasteStatementScreen
                        language={onboardingLanguage}
                        onNext={advanceOnboarding}
                        progressLabel={getOnboardingProgressLabel(ONBOARDING_IMPROVEMENT_STEP, ...onboardingVisibility)}
                        variant="improvement"
                    />
                )}

                {showOnboarding && !isOnboardingEvaluation && onboardingStep === ONBOARDING_CONTACT_STEP && (
                    <OnboardingTasteStatementScreen
                        language={onboardingLanguage}
                        onNext={advanceOnboarding}
                        progressLabel={getOnboardingProgressLabel(ONBOARDING_CONTACT_STEP, ...onboardingVisibility)}
                        variant="contact"
                    />
                )}

                {showOnboarding && !isOnboardingEvaluation && onboardingStep === ONBOARDING_LIFETIME_STEP && (
                    <OnboardingTasteStatementScreen
                        language={onboardingLanguage}
                        onNext={advanceOnboarding}
                        progressLabel={getOnboardingProgressLabel(ONBOARDING_LIFETIME_STEP, ...onboardingVisibility)}
                        variant="lifetime"
                    />
                )}

                {showOnboarding && !isOnboardingEvaluation && showOnboardingStudyPage && onboardingStep === ONBOARDING_STUDY_STEP && (
                    <UFOVOnboardingScreen
                        canContinue
                        demoReady={onboardingDemoReady}
                        demoVariantId={onboardingDemoVariantId}
                        demoTrial={onboardingDemoTrial}
                        language={onboardingLanguage}
                        onDemoPrewarmReady={handleOnboardingDemoPrewarmReady}
                        onNext={advanceOnboarding}
                        onSelectCenter={handleExampleCenterSelect}
                        onSelectPosition={handleExamplePositionSelect}
                        positions={POSITIONS}
                        progressStepIndex={getOnboardingVisibleStepIndex(onboardingStep, ...onboardingVisibility)}
                        selectedCenter={exampleCenterSelection}
                        selectedPosition={examplePositionSelection}
                        stepIndex={onboardingStep - ONBOARDING_INTRO_STEP}
                        totalSteps={onboardingTotalSteps}
                        wrongNoticeKey={visibleOnboardingNoticeKey}
                    />
                )}

                {showOnboarding && !isOnboardingEvaluation && showOnboardingResearchPage && onboardingStep === ONBOARDING_RESEARCH_STEP && (
                    <UFOVOnboardingScreen
                        canContinue
                        demoReady={onboardingDemoReady}
                        demoVariantId={onboardingDemoVariantId}
                        demoTrial={onboardingDemoTrial}
                        language={onboardingLanguage}
                        onDemoPrewarmReady={handleOnboardingDemoPrewarmReady}
                        onNext={advanceOnboarding}
                        onSelectCenter={handleExampleCenterSelect}
                        onSelectPosition={handleExamplePositionSelect}
                        positions={POSITIONS}
                        progressStepIndex={getOnboardingVisibleStepIndex(onboardingStep, ...onboardingVisibility)}
                        selectedCenter={exampleCenterSelection}
                        selectedPosition={examplePositionSelection}
                        stepIndex={onboardingStep - ONBOARDING_INTRO_STEP}
                        totalSteps={onboardingTotalSteps}
                        wrongNoticeKey={visibleOnboardingNoticeKey}
                    />
                )}

                {showOnboarding && !isOnboardingEvaluation && showOnboardingReviewPage && onboardingStep === ONBOARDING_REVIEW_STEP && (
                    <UFOVOnboardingReviewScreen
                        feedbackComment={reviewFeedback.comment}
                        feedbackError={reviewFeedback.error}
                        feedbackRating={reviewFeedback.rating}
                        feedbackOpen={reviewFeedback.open}
                        feedbackSubmitting={reviewFeedback.submitting}
                        language={onboardingLanguage}
                        onFeedbackCancel={skipReviewFeedback}
                        onFeedbackCommentChange={updateReviewFeedbackComment}
                        onFeedbackRatingChange={updateReviewFeedbackRating}
                        onFeedbackSubmit={() => void submitOnboardingReviewFeedback()}
                        onNext={advanceOnboarding}
                        progressLabel={getOnboardingProgressLabel(ONBOARDING_REVIEW_STEP, ...onboardingVisibility)}
                    />
                )}

                {!showOnboarding && showPostPurchase && (
                    <UFOVPostPurchaseScreen
                        language={onboardingLanguage}
                        onNext={advancePostPurchase}
                        stepIndex={postPurchaseStep}
                        totalSteps={POST_PURCHASE_TOTAL_STEPS}
                    />
                )}

                {!showOnboarding && !showPostPurchase && isPaywallVisible && (
                    <UFOVPaywallScreen
                        busyAction={billingBusyAction}
                        description={
                            paywallState.reason === 'onboarding-required'
                                ? paywallScreenConfig.onboardingDescription || paywallScreenConfig.description
                                : paywallScreenConfig.description
                        }
                        isPremium={hasTrainingAccess}
                        language={onboardingLanguage}
                        onClose={closePaywall}
                        onPurchase={(planId) => void handlePlanPurchase(planId)}
                        onRestore={() => void handleRestorePurchases()}
                        onSelectPlan={setSelectedPlan}
                        plans={planViews}
                        privacyHref={appRoutes.privacy || '/privacy'}
                        selectedPlan={selectedPlan}
                        showClose={canCloseVisiblePaywall}
                        statusMessage={billingStatus}
                        termsHref={appRoutes.terms || '/terms'}
                        title={
                            paywallState.reason === 'onboarding-required'
                                ? paywallScreenConfig.onboardingTitle || paywallScreenConfig.title
                                : paywallScreenConfig.title
                        }
                        variant={paywallState.reason === 'onboarding-required' ? 'onboarding' : 'menu'}
                    />
                )}

                {!showOnboarding && !showPostPurchase && !paywallState.open && phase === PHASES.MENU && (
                    <UFOVMenuScreen
                        activeMode={activeMode}
                        brainGameLevels={progress.brainGameLevels}
                        brainGameMenuItems={localizedBrainGameMenuItems}
                        dailyProgressByItem={dailyProgressByItem}
                        dailyTarget={dailyTarget}
                        ageEstimateIsCanonical={hasCanonicalEstimatedAge}
                        estimatedAge={estimatedAge}
                        language={onboardingLanguage}
                        modeLevels={{
                            'sopt-classic': classicProgress.level,
                            'sopt-advanced': advancedProgress.level,
                        }}
                        onConfig={() => {
                            setConfigInitialView('options');
                            setPhase(PHASES.CONFIG);
                        }}
                        onFeedback={feedbackEnabled ? () => {
                            setConfigInitialView('feedback');
                            setPhase(PHASES.CONFIG);
                        } : null}
                        onHelp={openMenuExplainer}
                        onSelectBrainGame={handleBrainGameSelect}
                        onChangeLevel={handleLevelChange}
                        onProgress={() => setPhase(PHASES.PROGRESS)}
                        onSelectMode={handleModeSelect}
                        onStart={startSelectedMenuItem}
                        selectedBrainGameId={selectedBrainGameId}
                        startDisabled={selectedBrainGameId ? false : symbolVariant.preloadAssets.length > 0 && !variantAssetsReady}
                        startLabel={selectedBrainGameId
                            ? uiCopy.startGame
                            : symbolVariant.preloadAssets.length > 0 && !variantAssetsReady
                            ? (symbolVariant.warmupLabel || uiCopy.preparingPack)
                            : uiCopy.startRun}
                        tourStep={activeMenuExplainerStep}
                        onTourDismiss={dismissMenuExplainer}
                        onTourNext={advanceMenuExplainer}
                    />
                )}

                {!showOnboarding && !showPostPurchase && !paywallState.open && phase === PHASES.CONFIG && (
                    <UFOVConfigScreen
                        billingBusyAction={billingBusyAction}
                        colorBlindModeEnabled={colorBlindModeEnabled}
                        countdownHapticsEnabled={countdownHapticsEnabled}
                        feedbackSource={configInitialView === 'feedback' ? 'home_contact' : 'settings'}
                        feedbackEnabled={feedbackEnabled}
                        initialView={feedbackEnabled ? configInitialView : 'options'}
                        language={onboardingLanguage}
                        onColorBlindModeChange={handleColorBlindModeChange}
                        managementHref={premiumState.managementUrl || ''}
                        onCountdownHapticsChange={handleCountdownHapticsChange}
                        onLanguageChange={handleOnboardingLanguageChange}
                        onResetAll={handleReset}
                        onRestorePurchases={() => void handleRestorePurchases()}
                        onSubmitFeedback={submitSettingsFeedback}
                        onClose={returnToMenu}
                        onSelectedAssetThemesChange={handleSelectedAssetThemesChange}
                        privacyHref={appRoutes.privacy || '/privacy'}
                        restoreStatusMessage={billingStatus}
                        selectedAssetThemeIds={selectedAssetThemeIds}
                        termsHref={appRoutes.terms || '/terms'}
                        themes={assetThemeOptions}
                    />
                )}

                {!showOnboarding && !showPostPurchase && !paywallState.open && phase === PHASES.PROGRESS && (
                    <UFOVProgressScreen
                        brainGameHighScores={progress.brainGameHighScores}
                        brainGameMenuItems={localizedBrainGameMenuItems}
                        gameScoreHistory={progress.gameScoreHistory}
                        language={onboardingLanguage}
                        modeProgress={progress.modes}
                        onContinue={returnToMenu}
                        onMenu={returnToMenu}
                    />
                )}

                {!showOnboarding && !showPostPurchase && !paywallState.open && isLiveTrialPhase(phase) && trial && (
                    <UFOVPlayScreen language={onboardingLanguage} phase={phase} trial={trial} onPrewarmReady={handleBoardPrewarmReady} />
                )}

                {shouldMountCenterQuestion && (
                    <div
                        aria-hidden={isCenterQuestionPhase ? undefined : 'true'}
                        className={`UFOV-game__persistent-screen-layer ${isCenterQuestionPhase ? 'is-visible' : 'is-prewarming'}`.trim()}
                        data-ufov-question-prewarm="true"
                        inert={isCenterQuestionPhase ? undefined : ''}
                    >
                        <UFOVCenterQuestionScreen
                            language={onboardingLanguage}
                            onContinue={() => setPhase(activeModeConfig.questionCount >= 3 ? PHASES.ANSWER_PERIPHERAL : PHASES.ANSWER_POSITION)}
                            onMissedIt={() => setMissedItDialogOpen(true)}
                            onSelectCenter={handleCenterAnswerSelect}
                            selectedCenter={selectedCenter}
                            showFooter={false}
                            step={centerStep}
                            trial={trial}
                        />
                    </div>
                )}

                {shouldMountPositionQuestion && (
                    <div
                        aria-hidden={isPositionQuestionPhase ? undefined : 'true'}
                        className={`UFOV-game__persistent-screen-layer ${isPositionQuestionPhase ? 'is-visible' : 'is-prewarming'}`.trim()}
                        data-ufov-question-prewarm="true"
                        inert={isPositionQuestionPhase ? undefined : ''}
                    >
                        <UFOVPositionQuestionScreen
                            buttonLabel={uiCopy.scoreRun}
                            language={onboardingLanguage}
                            onContinue={submitAnswer}
                            onMissedIt={() => setMissedItDialogOpen(true)}
                            onSelectPosition={handlePositionAnswerSelect}
                            positions={POSITIONS}
                            selectedPosition={selectedPosition}
                            showFooter={false}
                            step={positionStep}
                            trial={trial}
                        />
                    </div>
                )}

                {shouldMountPeripheralQuestion && (
                    <div
                        aria-hidden={isPeripheralQuestionPhase ? undefined : 'true'}
                        className={`UFOV-game__persistent-screen-layer ${isPeripheralQuestionPhase ? 'is-visible' : 'is-prewarming'}`.trim()}
                        data-ufov-question-prewarm="true"
                        inert={isPeripheralQuestionPhase ? undefined : ''}
                    >
                        <UFOVPeripheralQuestionScreen
                            buttonLabel={uiCopy.next}
                            language={onboardingLanguage}
                            onContinue={() => setPhase(PHASES.ANSWER_POSITION)}
                            onMissedIt={() => setMissedItDialogOpen(true)}
                            onSelectPeripheral={handlePeripheralAnswerSelect}
                            selectedPeripheral={selectedPeripheral}
                            showFooter={false}
                            step={peripheralStep}
                            trial={trial}
                        />
                    </div>
                )}

                {!showOnboarding && !showPostPurchase && !paywallState.open && phase === PHASES.RESULT && (
                    <UFOVResultScreen
                        feedback={feedback}
                        language={onboardingLanguage}
                        onMenu={returnToMenu}
                        onNext={() => void beginTrial()}
                        progress={activeProgress}
                        trial={trial}
                    />
                )}
                <UFOVMissedItDialog
                    language={onboardingLanguage}
                    onCancel={() => setMissedItDialogOpen(false)}
                    onRetry={handleMissedItRetry}
                    open={missedItDialogOpen}
                />
                    </>
                )}
                </div>
            </div>
        </UFOVSymbolVariantProvider>
    );
}
